import { formatPlanningBufferLabel } from '@/utils/planningBuffer';
import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { EFFICIENCY_NAMES } from '@/lib/efficiencyNames'; // CORE-G GAP-005: one name, one meaning
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { TopBar } from '@/components/layout/TopBar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';
import { toast } from '@/hooks/use-toast';
import { logPolicyChange } from '@/utils/ticketEventLogger';
import { triggerBoardCascade } from '@/utils/cascadeTrigger';
import { getTodayInShopTz } from '@/utils/shopTime';
import { Loader2, Save, Building2, Clock, Calendar as CalendarIcon, ChevronLeft, ChevronRight, User, Users, TrendingUp, Wrench, Trash2, Plus, Edit, Award, UserPlus, MapPin, CloudSun, DollarSign, Package, Bell, ClipboardList, Settings, Eye, GraduationCap, ExternalLink, Handshake, ShieldCheck } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LoopHealthPanel } from '@/components/intelligence/LoopHealthPanel';
import { AutomationPosturePanel } from '@/components/intelligence/AutomationPosturePanel';
import { Checkbox } from '@/components/ui/checkbox';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, getDay } from 'date-fns';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { cn } from '@/lib/utils';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { TechnicianForm } from '@/components/technicians/TechnicianForm';
import { importVehicleCatalog } from '@/utils/importVehicleCatalog';
import { useAuth } from '@/contexts/AuthContext';
import { BetaParticipationCard } from '@/components/beta/BetaParticipationCard';
import { useTutorial } from '@/contexts/TutorialContext';
import { usePermissions } from '@/hooks/usePermissions';
import { useRosterTechnicianPassports } from '@/hooks/passport/useRosterTechnicianPassports';
import { useIsInternalAdmin } from '@/hooks/useIsInternalAdmin';
import { useFormFactor } from '@/hooks/use-mobile';
import { ReminderSettings } from '@/components/reminders/ReminderSettings';
import { ReminderAnalytics } from '@/components/reminders/ReminderAnalytics';
import { SuppressedReminderReasons } from '@/components/reminders/SuppressedReminderReasons';
// TCX-7 — shop-scoped training domain-match policy settings.
import { TrainingDomainPolicyShopSettings } from '@/components/training/TrainingDomainPolicyShopSettings';
import { BillingPanel } from '@/components/billing/BillingPanel';
import { CloseShopCard } from '@/components/settings/CloseShopCard';
import { actsAsLegacyRole } from '@/lib/roles/multiRole';
import { AlertSettings } from '@/components/metrics/AlertSettings';
import { AlertHistoryWidget } from '@/components/metrics/AlertHistoryWidget';
import { resolveSkillTier, PROFICIENCY_CATEGORIES } from '@/constants/proficiencyCategories';
import { StaffScheduleManager } from '@/components/timeclock/StaffScheduleManager';
import { PensieveSettingsCard } from '@/components/scheduling/PensieveSettingsCard';
import { FohAttentionSettingsCard } from '@/components/foh/FohAttentionSettingsCard';
import { AlertThresholdsCard } from '@/components/settings/AlertThresholdsCard';
import {
  applyShopProfileCulture,
  buildShopProfileCulturePatch,
  normalizeSpecialistGapSplitting,
} from '@/lib/shopCulture/shopProfileCulture';
import { persistShopCulturePatch } from '@/lib/shopCulture/persistShopCulturePatch';
import { SHOP_CULTURE_DEFAULTS, cultureNumber, cultureBoolean } from '@/lib/shopCulture/shopCultureDefaults';
import { DlgThresholdSettings } from '@/components/settings/DlgThresholdSettings';
import { PhoneIntegrationSettings } from '@/components/settings/PhoneIntegrationSettings';
import { formatTimeString } from '@/utils/formatShopTime';
import { SliderHelpTooltip } from '@/components/ui/SliderHelpTooltip';
import { SHOP_CULTURE_HELP } from '@/constants/sliderHelpContent';
import { OptiAssetsToggle } from '@/components/optiassets/OptiAssetsToggle';
import { ShowFeature } from '@/components/access/ShowFeature';
import { OptiBayConnectToggle } from '@/components/connect/OptiBayConnectToggle';
import { MultiTeamToggle } from '@/components/teams/MultiTeamToggle';
import { TeamList } from '@/components/teams/TeamList';
import { useMultiTeam } from '@/hooks/useMultiTeam';
import { useOptiAssets } from '@/hooks/useOptiAssets';
import { useOptiDvi } from '@/hooks/useOptiDvi';
import { FormerMembersTab } from '@/components/shop/FormerMembersTab';
import { ExternalIntegrationsTab } from '@/components/integrations/ExternalIntegrationsTab';
import { TaxesFeesTab } from '@/components/shop/taxes-fees/TaxesFeesTab';
import { PricingControlTab } from '@/components/shop/pricing-control/PricingControlTab';
import { LaborPricingTab } from '@/components/shop-profile/LaborPricingTab';
import { FinancialControlsSection } from '@/components/shop-profile/labor-pricing/FinancialControlsSection';
import { InvoicingSettingsTab } from '@/components/shop-profile/InvoicingSettingsTab';
import { useFeatureFlag, useExposureFlag } from '@/hooks/useFeatureFlags';
import { hasVariableWorkingHours } from '@/utils/technicianScheduleVariance';

const TimeClockReports = lazy(() => import('@/pages/TimeClockReports'));

/** Time Clock Reports render inline in Admin Settings (no standalone route since 54a4851c0). */
const TIME_CLOCK_REPORTS_ANCHOR = 'time-clock-reports';
const TIME_CLOCK_REPORTS_HREF = `/shop-profile?tab=admin#${TIME_CLOCK_REPORTS_ANCHOR}`;

const shopProfileSchema = z.object({
  name: z.string().trim().min(1, 'Shop name is required').max(100, 'Name must be less than 100 characters'),
  address: z.string().trim().max(200, 'Address must be less than 200 characters').optional(),
  streetAddress: z.string().trim().max(200, 'Street address must be less than 200 characters').optional(),
  city: z.string().trim().max(100, 'City must be less than 100 characters').optional(),
  state: z.string().trim().max(50, 'State must be less than 50 characters').optional(),
  postalCode: z.string().trim().max(20, 'Postal code must be less than 20 characters').optional(),
  country: z.string().trim().max(100, 'Country must be less than 100 characters').optional(),
  timeZone: z.string().trim().min(1, 'Time zone is required'),
  vehiclePackages: z.array(z.string()).default([]),
  shopCulture: z.object({
    willingnessToReassign: z.number().min(0).max(100),
    specialistGapSplitting: z.number().min(0).max(10),
    workloadBalancePreference: z.number().min(0).max(1),
    comebackNudge: z.number().min(0).max(100),
    willingnessRollPastEod: z.number().min(0).max(120),
    presentVehiclePriority: z.number().min(0).max(120),
    ticketSplittingEnabled: z.boolean(),
    minSplitMinutes: z.number().min(15).max(120),
    crossTechFitnessThreshold: z.number().min(0).max(100),
    proficiencyGapThreshold: z.number().min(0).max(100),
    waiterTimeFlexMin: z.number().min(0).max(30),
    // Bucket 5 §5.5.4 — No-show grace threshold (Shop Culture setting).
    // Foundational only in Slice 0.3a; not yet wired into scheduling behavior.
    noShowGraceThresholdMin: z.number().min(0).max(120),
    // Core-100 R4 CORE-BI (GAP-001): EOD-risk flag lead time; server range 0-120.
    eodRiskThresholdMin: z.number().min(0).max(120),
    // Core-100 CORE-BO (GAP-004): may opened capacity be filled with work scheduled for a LATER day?
    allowFutureDayPullForward: z.boolean().optional(),
    // oct20e (FD-2026-10-24-A): automatic capacity recovery (default) or manual suggestion mode.
    capacityRecoveryAutomatic: z.boolean().optional(),
    // Core-100 R5 (CORE-BI x CORE-I): weigh demonstrated diagnostic competency for diagnostic work.
    diagnosticWeightEnabled: z.boolean().optional(),
    outdoorWeatherSettings: z.object({
      enabled: z.boolean(),
      minOutdoorTempF: z.number().min(-40).max(120),
      blockWhenRaining: z.boolean(),
      blockWhenSnowing: z.boolean(),
      blockWhenStorms: z.boolean(),
    }).optional(),
  }).optional(),
});

type ShopProfileValues = z.infer<typeof shopProfileSchema>;

const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/**
 * Batch 14 (2026-05-29): defensive normalizer for the Shop Culture
 * "Skill Tier Gap for Ticket Splitting" slider.
 *
 * History:
 *   - Migration 20251024021603 created `shop_profile.shop_culture` jsonb
 *     with column DEFAULT `{"specialist_gap_splitting": 15, ...}` when
 *     the slider's product range was 0-100.
 *   - The slider was later tightened to 0-10 in the React zod schema
 *     (shopProfileValidation.ts + ShopProfile.tsx form schema).
 *   - Migration 20251122212334 cleaned EXISTING rows from 15 → 5 but
 *     did NOT change the column DEFAULT, so every shop created after
 *     Nov 2025 (including the smoke-test shop) still gets `15` written
 *     by the column default. The form's `?? 5` null-coalesce passes
 *     `15` through unchanged → fails zod `max(10)` → silent validation
 *     abort → "Save Profile button stopped saving" (Batch 13 surface).
 *
 * Rules (per the operator's Batch 14 spec):
 *   - null / undefined / non-number / NaN → 3 (the new product default)
 *   - exactly 15                          → 3 (legacy column DEFAULT —
 *                                              "clearly default-created"
 *                                              per the spec, so reset)
 *   - < 0                                 → 0 (clamp)
 *   - > 10                                → 10 (clamp; preserves any
 *                                              operator-set high value
 *                                              that isn't the legacy 15)
 *   - otherwise                           → unchanged
 *
 * Used at three load sites (form defaults, existing-data branch,
 * no-data branch) AND at the save-payload assembly site so a stale
 * legacy value can never block save and a future state regression
 * can never write an invalid value back to the DB.
 */
// Moved to `src/lib/shopCulture/shopProfileCulture.ts` — it normalises a
// stored shop-culture value, and this page is not the authority on that.
// Imported above; the full doctrine comment travels with the function.

const bayTypes = ['Lift', 'Flat', 'Alignment', 'Specialty'];
const specialtyOptions = ['Diesel', 'Electrical', 'Diagnostics', 'Body & Paint', 'Performance', 'Transmission'];

const vehiclePackages = [
  { 
    id: 'domestic', 
    label: 'Domestic',
    description: 'Chrysler, Dodge, Jeep, Ram, Ford, Lincoln, Mercury, Chevrolet, GMC, Buick, Cadillac, etc.'
  },
  { 
    id: 'asian', 
    label: 'Asian',
    description: 'Toyota, Honda, Nissan, Mazda, Subaru, Hyundai, Kia, Mitsubishi, Lexus, Acura, Infiniti, Genesis, etc.'
  },
  { 
    id: 'euro', 
    label: 'Euro',
    description: 'BMW, Mercedes-Benz, Audi, Volkswagen, Porsche, Volvo, Land Rover, etc.'
  },
  { 
    id: 'ev', 
    label: 'EV',
    description: 'Tesla, Rivian, Lucid, Polestar, and all electric vehicles'
  },
  { 
    id: 'exotic', 
    label: 'Exotic/Supercar',
    description: 'Ferrari, Lamborghini, Maserati, Bentley, Rolls-Royce, McLaren, Aston Martin, Bugatti, etc.'
  },
  { 
    id: 'light_duty_diesel', 
    label: 'Light Duty Diesel',
    description: 'Diesel trucks and SUVs from Ford, Ram, Chevrolet, GMC, etc.'
  },
];

interface Technician {
  hours?: Json | null;
  id: string;
  name: string;
  role: string;
  skill_tier: number;
  shift_start: string;
  shift_end: string;
  employment_status: string;
  is_present: boolean;
  bay_assignments: string[];
  specialties: string[];
  proficiencies: Record<string, number>;
  efficiency_percentage?: number;
  buffer_percentage?: number;
  technician_profile_id?: string;
}

const ShopProfile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { activeShop, user, membership } = useAuth();
  const isShopOwner = actsAsLegacyRole(membership as never, 'OWNER');
  const { isActive, currentStepData, nextStep } = useTutorial();
  const permissions = usePermissions();
  const { canEditShopProfile, canEditTechnicians, canManageBilling, canViewPayrollReports, canEditInspectionTemplates, canViewTimeClockReports, canManageExternalIntegrations } = permissions;
  const { data: isInternalAdmin } = useIsInternalAdmin();
  const optiAssets = useOptiAssets();
  const optiDvi = useOptiDvi();
  const formFactor = useFormFactor();
  const isMobile = formFactor === 'phone';
  // Phase 1: dual gate for Taxes & Fees tab
  const partsFinancialEngine = useFeatureFlag('partsFinancialEngine');
  const uiShopProfileTaxesFees = useExposureFlag('ui_shopProfileTaxesFees');
  const showTaxesFees = canEditShopProfile && partsFinancialEngine && uiShopProfileTaxesFees;
  // Phase 10 R1: dual gate for Labor Pricing tab
  const laborEngineEnabled = useFeatureFlag('labor_engine_enabled');
  const uiLaborPricing = useExposureFlag('ui_labor_pricing');
  const showLaborPricing = canEditShopProfile && laborEngineEnabled && uiLaborPricing;
  const uiCustomerInvoices = useExposureFlag('ui_customer_invoices');
  const showInvoicing = canEditShopProfile && uiCustomerInvoices;
  
  // Refs and state for scrollable tabs
  const tabsScrollRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [shopId, setShopId] = useState<string | null>(null);
  const [hours, setHours] = useState<Record<string, { open: string; close: string }>>({});
  const [openDays, setOpenDays] = useState<Record<string, boolean>>({});
  const [holidays, setHolidays] = useState<Array<{ date: string; name: string }>>([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  // Technician management state
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  // Oct 20 audit A1: each roster row's Technician Passport target, resolved in
  // two batch reads under the viewer's RLS. Never the viewer's own Passport for
  // somebody else; unavailable (with a reason) when the server returns no
  // profile this viewer may open.
  const { data: techPassportTargets } = useRosterTechnicianPassports(activeShop?.id, technicians, user?.id);
  const [selectedTechId, setSelectedTechId] = useState<string | null>(null);
  const [isEditingTech, setIsEditingTech] = useState(false);
  const [techToDelete, setTechToDelete] = useState<string | null>(null);
  const [isImportingCatalog, setIsImportingCatalog] = useState(false);
  const [previousPackages, setPreviousPackages] = useState<string[]>([]);
  const [shopProfileData, setShopProfileData] = useState<any>(null);
  // Sync button removed - profiles are now auto-created with technicians

  const form = useForm<ShopProfileValues>({
    resolver: zodResolver(shopProfileSchema),
    defaultValues: {
      name: '',
      address: '',
      streetAddress: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'United States',
      timeZone: 'America/Los_Angeles',
      vehiclePackages: [],
      shopCulture: {
        willingnessToReassign: 50,
        // Batch 14: was 5, now 3 (the new product default per the
        // Batch 14 spec). normalizeSpecialistGapSplitting(undefined)
        // yields 3, but we inline the literal here so the form's
        // declarative default reads cleanly.
        specialistGapSplitting: 3,
        workloadBalancePreference: 0.5,
        comebackNudge: 100,
        willingnessRollPastEod: 0,
        presentVehiclePriority: SHOP_CULTURE_DEFAULTS.present_vehicle_priority,
        ticketSplittingEnabled: true,
        minSplitMinutes: 30,
        crossTechFitnessThreshold: 20,
        proficiencyGapThreshold: 40,
        waiterTimeFlexMin: 0,
        // oct20e (FD-2026-10-24-A): both on by default (shared defaults table)
        allowFutureDayPullForward: SHOP_CULTURE_DEFAULTS.allow_future_day_pull_forward,
        capacityRecoveryAutomatic: SHOP_CULTURE_DEFAULTS.capacity_recovery_automatic,
        noShowGraceThresholdMin: 15,
        eodRiskThresholdMin: SHOP_CULTURE_DEFAULTS.eod_risk_threshold_min,
        diagnosticWeightEnabled: SHOP_CULTURE_DEFAULTS.diagnostic_weight_enabled,
        outdoorWeatherSettings: {
          enabled: false,
          minOutdoorTempF: 40,
          blockWhenRaining: true,
          blockWhenSnowing: true,
          blockWhenStorms: true,
        },
      },
    },
  });

  // Scroll handlers for tabs
  const SCROLL_AMOUNT = 150;
  
  const scrollTabsLeft = () => {
    tabsScrollRef.current?.scrollBy({ left: -SCROLL_AMOUNT, behavior: 'smooth' });
  };
  
  const scrollTabsRight = () => {
    tabsScrollRef.current?.scrollBy({ left: SCROLL_AMOUNT, behavior: 'smooth' });
  };
  
  const handleTabsScroll = () => {
    const el = tabsScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth);
  };

  useEffect(() => {
    const loadShopProfile = async () => {
      try {
        const { data, error } = await supabase
          .from('shop_profile')
          .select('*')
          .eq('shop_id', activeShop?.id)
          .maybeSingle();

        if (error) throw error;

        if (data) {
          setShopId(data.id);
          setShopProfileData(data);
          form.reset({
            name: data.name,
            address: data.address || '',
            streetAddress: data.street_address || '',
            city: data.city || '',
            state: data.state || '',
            postalCode: data.postal_code || '',
            country: data.country || 'United States',
            timeZone: data.time_zone,
            vehiclePackages: ((data.vehicle_packages as string[]) || []).filter((p) => p !== 'vintage'),
            shopCulture: data.shop_culture ? {
              // CORE-BI R4: an unset key shows the SAME shared default the server runs with.
              willingnessToReassign: cultureNumber(data.shop_culture, 'willingness_to_reassign'),
              // Batch 14: pass through the defensive normalizer so a
              // stored legacy 15 (or any other out-of-range value)
              // can't reach the form and block save via zod's max(10).
              specialistGapSplitting: normalizeSpecialistGapSplitting((data.shop_culture as any).specialist_gap_splitting),
              workloadBalancePreference: cultureNumber(data.shop_culture, 'workload_balance_preference'),
              comebackNudge: cultureNumber(data.shop_culture, 'comeback_nudge'),
              willingnessRollPastEod: cultureNumber(data.shop_culture, 'willingness_roll_past_eod'),
              presentVehiclePriority: cultureNumber(data.shop_culture, 'present_vehicle_priority'),
              ticketSplittingEnabled: cultureBoolean(data.shop_culture, 'ticket_splitting_enabled'),
              minSplitMinutes: cultureNumber(data.shop_culture, 'min_split_minutes'),
              crossTechFitnessThreshold: cultureNumber(data.shop_culture, 'cross_tech_fitness_threshold'),
              proficiencyGapThreshold: cultureNumber(data.shop_culture, 'proficiency_gap_threshold'),
              waiterTimeFlexMin: cultureNumber(data.shop_culture, 'waiter_time_flex_min'),
              // Bucket 5 §5.5.4 — read no_show_grace_threshold_min from shop_culture.
              noShowGraceThresholdMin: cultureNumber(data.shop_culture, 'no_show_grace_threshold_min'),
              eodRiskThresholdMin: cultureNumber(data.shop_culture, 'eod_risk_threshold_min'),
              allowFutureDayPullForward: cultureBoolean(data.shop_culture, 'allow_future_day_pull_forward'),
              capacityRecoveryAutomatic: cultureBoolean(data.shop_culture, 'capacity_recovery_automatic'),
              diagnosticWeightEnabled: cultureBoolean(data.shop_culture, 'diagnostic_weight_enabled'),
              outdoorWeatherSettings: (data.shop_culture as any).outdoor_weather_settings ? {
                enabled: (data.shop_culture as any).outdoor_weather_settings.enabled ?? false,
                minOutdoorTempF: (data.shop_culture as any).outdoor_weather_settings.min_outdoor_temp_f ?? 40,
                blockWhenRaining: (data.shop_culture as any).outdoor_weather_settings.block_when_raining ?? true,
                blockWhenSnowing: (data.shop_culture as any).outdoor_weather_settings.block_when_snowing ?? true,
                blockWhenStorms: (data.shop_culture as any).outdoor_weather_settings.block_when_storms ?? true,
              } : {
                enabled: false,
                minOutdoorTempF: 40,
                blockWhenRaining: true,
                blockWhenSnowing: true,
                blockWhenStorms: true,
              },
            } : {
              willingnessToReassign: 50,
              // Batch 14: was 5, now 3 (matches the form defaultValues).
              specialistGapSplitting: 3,
              workloadBalancePreference: 0.5,
              comebackNudge: 100,
              willingnessRollPastEod: 0,
              presentVehiclePriority: SHOP_CULTURE_DEFAULTS.present_vehicle_priority,
              ticketSplittingEnabled: true,
              minSplitMinutes: 30,
              crossTechFitnessThreshold: 20,
              proficiencyGapThreshold: 40,
              waiterTimeFlexMin: 0,
              noShowGraceThresholdMin: 15,
              eodRiskThresholdMin: SHOP_CULTURE_DEFAULTS.eod_risk_threshold_min,
              allowFutureDayPullForward: SHOP_CULTURE_DEFAULTS.allow_future_day_pull_forward,
              capacityRecoveryAutomatic: SHOP_CULTURE_DEFAULTS.capacity_recovery_automatic,
              diagnosticWeightEnabled: SHOP_CULTURE_DEFAULTS.diagnostic_weight_enabled,
              outdoorWeatherSettings: {
                enabled: false,
                minOutdoorTempF: 40,
                blockWhenRaining: true,
                blockWhenSnowing: true,
                blockWhenStorms: true,
              },
            },
          });
          // Batch 12 (2026-05-29): case-tolerant hours load.
          //
          // `commitWizard.ts` (line 54 comment) deliberately writes
          // `shop_profile.hours` with lowercase day keys
          // (`monday`..`sunday`) — that's the convention the
          // technicianHoursAdvisory + shopHoursUtils helpers already
          // accept via case-tolerant readers. This page's UI + save
          // path, however, keys by the capitalized `daysOfWeek` array
          // declared at file top (`Monday`..`Sunday`). Pre-bump, an
          // onboarding-written row's `hours.Monday` lookup was always
          // undefined → every day appeared closed in the form +
          // calendar, and the user's smoke test surfaced it as
          // "Hours & Holidays shows all days unchecked".
          //
          // Fix: read both formats during load. Whichever the row
          // carries, normalize into the capitalized-key shape the
          // page state expects. The save path (L399-419) continues
          // to write capitalized keys; first save after this load
          // re-canonicalizes the row to a single convention without
          // any downstream consumer change. Manual-setup-then-save
          // rows that already carry capitalized keys work unchanged.
          const rawHoursData = (data.hours as Record<string, { open: string; close: string }>) || {};
          const hoursData: Record<string, { open: string; close: string }> = {};
          const openDaysMap: Record<string, boolean> = {};
          for (const day of daysOfWeek) {
            const entry = rawHoursData[day] ?? rawHoursData[day.toLowerCase()];
            if (entry?.open && entry?.close) {
              hoursData[day] = { open: entry.open, close: entry.close };
              openDaysMap[day] = true;
            } else {
              openDaysMap[day] = false;
            }
          }
          setHours(hoursData);
          setOpenDays(openDaysMap);
          
          setHolidays((data.holidays as Array<{ date: string; name: string }>) || []);
          setPreviousPackages(((data.vehicle_packages as string[]) || []).filter((p) => p !== 'vintage'));
        }
      } catch (error: any) {
        toast({
          title: 'Error loading shop profile',
          description: error.message,
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadShopProfile();
    loadTechnicians();
  }, [activeShop?.id]);
  
  // Initialize scroll state
  useEffect(() => {
    handleTabsScroll();
  }, []);

  // Batch 13 (2026-05-29): silent-validation-fail surface. Pre-bump,
  // `form.handleSubmit(onSubmit)` was wired without an error callback;
  // any zod failure on `shopProfileSchema` (e.g. an empty timeZone
  // captured from a partially-populated shop_profile row, a
  // shopCulture slider out of range, an empty name) caused
  // handleSubmit to silently abort — onSubmit never ran, no toast
  // fired, no console line, no save. Symptom: "Save Profile button
  // stopped saving" on the smoke test.
  //
  // This handler enumerates the failing field paths and surfaces a
  // destructive toast so the user sees the actual blocker.
  const onValidationError = (errors: Record<string, unknown>) => {
    // Walk one level deep so nested shopCulture errors are visible.
    const fieldNames: string[] = [];
    const walk = (prefix: string, node: unknown) => {
      if (!node || typeof node !== 'object') return;
      for (const key of Object.keys(node as Record<string, unknown>)) {
        const child = (node as Record<string, unknown>)[key];
        if (child && typeof child === 'object' && 'message' in (child as Record<string, unknown>)) {
          fieldNames.push(prefix ? `${prefix}.${key}` : key);
        } else if (child && typeof child === 'object') {
          walk(prefix ? `${prefix}.${key}` : key, child);
        }
      }
    };
    walk('', errors);
    console.warn('[ShopProfile.onSubmit] validation failed', { fieldNames, errors });
    toast({
      title: 'Cannot save — please fix the highlighted fields',
      description: fieldNames.length > 0
        ? `Errors in: ${fieldNames.join(', ')}`
        : 'Some fields are invalid.',
      variant: 'destructive',
    });
  };

  const onSubmit = async (values: ShopProfileValues) => {
    setIsSaving(true);
    console.info('[ShopProfile.onSubmit] save clicked', {
      shopId,
      activeShopId: activeShop?.id,
      // Only key names — never values. Avoids logging customer name /
      // street address / lat-lng or any other PII.
      hoursOpenDayCount: daysOfWeek.filter((d) => openDays[d]).length,
    });
    try {
      // Geocode address if changed
      const fullAddress = [values.streetAddress, values.city, values.state, values.postalCode, values.country]
        .filter(Boolean)
        .join(', ');
      
      let shopLat = shopProfileData?.shop_lat || null;
      let shopLng = shopProfileData?.shop_lng || null;
      
      // Only geocode if we have meaningful address parts (not just country)
      const hasMeaningfulAddress = [values.streetAddress, values.city, values.state, values.postalCode].some(v => v && v.trim().length > 0);
      if (hasMeaningfulAddress && fullAddress && fullAddress !== shopProfileData?.address) {
        try {
          const geocodeResponse = await supabase.functions.invoke('geocode-address', {
            body: { address: fullAddress }
          });
          if (geocodeResponse.data?.lat && geocodeResponse.data?.lng) {
            shopLat = geocodeResponse.data.lat;
            shopLng = geocodeResponse.data.lng;
            console.log('[SHOP-PROFILE] Geocoded address:', { lat: shopLat, lng: shopLng });
          }
        } catch (geoError) {
          console.warn('[SHOP-PROFILE] Geocoding failed:', geoError);
        }
      }

      // Build normalized hours payload — only include open days with valid times
      const normalizedHours: Record<string, { open: string; close: string }> = {};
      for (const day of daysOfWeek) {
        if (openDays[day] && hours[day]?.open && hours[day]?.close) {
          normalizedHours[day] = { open: hours[day].open, close: hours[day].close };
        }
      }

      // `shop_culture` is a SHARED, extensible JSON object. The board
      // optimizer, preference learning, weather gating and CORE-BN all
      // keep their configuration inside it alongside this form's fields.
      //
      // This page previously rebuilt the whole object from its own form
      // values and wrote it as a replacement, silently deleting every
      // other capability's keys on each ordinary save. It now reads the
      // current object and applies a patch of only the keys it owns.
      //
      // The read must happen HERE, not at page load: another surface (the
      // Front-of-House Load card sits on this very page) may have written
      // while the form was open.
      //
      // A failed read aborts the save. Proceeding would mean writing a
      // culture object built on an unknown baseline, which is the erasure
      // bug wearing a different hat — and a save that quietly destroys
      // configuration while reporting success is worse than one that
      // fails loudly.
      //
      // Core-100 R4 CORE-BI: the patch of the keys this form owns is now merged
      // ON THE SERVER (patch_shop_culture, one UPDATE) for an existing profile,
      // so a concurrent save on another card (FOH load, optimizer) can no longer
      // be lost between a browser read and write. Only the first-ever INSERT of
      // a profile row still builds a full object (there is nothing to merge into).
      const culturePatch = buildShopProfileCulturePatch(values.shopCulture);

      const profileData = {
        name: values.name,
        address: values.address || null,
        street_address: values.streetAddress || null,
        city: values.city || null,
        state: values.state || null,
        postal_code: values.postalCode || null,
        country: values.country || null,
        shop_lat: shopLat,
        shop_lng: shopLng,
        time_zone: values.timeZone,
        vehicle_packages: values.vehiclePackages,
        hours: normalizedHours,
        breaks: [],
        holidays,
        // shop_culture is NOT in this payload: it is written by the server-side
        // patch below (update) or added explicitly for a first-ever insert.
      };

      // Cancel any pending auto-save to prevent it from overwriting this save
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }

      let savedCulture: Record<string, unknown> | null = null;
      if (shopId && activeShop?.id) {
        // Culture first: a refused value (server range guard, 23514) stops the
        // whole save before anything else is written.
        if (Object.keys(culturePatch).length > 0) {
          savedCulture = await persistShopCulturePatch(supabase, activeShop.id, culturePatch);
        }
        console.info('[ShopProfile.onSubmit] update branch', {
          shopId,
          activeShopId: activeShop.id,
          payloadKeys: Object.keys(profileData),
        });
        const { data: updatedRow, error } = await supabase
          .from('shop_profile')
          .update({
            ...profileData,
            shop_id: activeShop.id
          })
          .eq('shop_id', activeShop.id)
          .select('id')
          .maybeSingle();
        if (error) {
          // Batch 13: structured logging matching the Birdseye Batch 10
          // pattern so the actual PostgREST shape (code / message /
          // details / hint) lands in the console instead of just the
          // generic Error.message string surfaced by the catch block
          // toast.
          const e = error as { code?: string; message?: string; details?: string; hint?: string };
          console.error('[ShopProfile.onSubmit] update failed', {
            code: e.code ?? null,
            message: e.message ?? null,
            details: e.details ?? null,
            hint: e.hint ?? null,
          });
          throw error;
        }
        if (!updatedRow) {
          console.warn('[ShopProfile.onSubmit] update returned no row — shop_id may not match an existing shop_profile', {
            shopId,
            activeShopId: activeShop.id,
          });
          throw new Error('No shop profile row was updated — please reload and try again.');
        }
        console.info('[ShopProfile.onSubmit] update succeeded', { row_id: updatedRow.id });

        // P6-A3 — wire shop_profile UPDATE success to canonical audit_log.
        //
        // Sister to P6-A2's MEMBERSHIP_ROLE_CHANGED. This INSERT runs
        // ONLY after the UPDATE succeeded (the `throw` above
        // short-circuits any failure). Action vocabulary:
        // SHOP_SETTING_UPDATED — second SCREAMING_SNAKE_CASE entry in
        // the high-risk-admin-action namespace P6-A2 established.
        //
        // PII safety: shop_profile contains the shop's full street
        // address, lat/lng, and other identifying fields. We log only
        // the LIST of changed top-level field NAMES (no values), so
        // the audit row captures intent + actor + scope without
        // echoing PII back into the audit trail. Compliance readers
        // needing post-state values can pull the canonical row from
        // shop_profile directly. The existing per-slider Pensieve
        // `logPolicyChange` calls below carry value-level transitions
        // for shop_culture sliders — that domain log is preserved
        // verbatim alongside this audit_log row, mirroring the dual-
        // audit pattern established by membership_role_events +
        // audit_log in P6-A2.
        //
        // Non-blocking semantics: matches P6-A2 + all 4 sister
        // audit_log call sites (useSupportAudit, useCreatorAdmin-
        // Analytics, TicketTransferDialog, smart-assign). On failure:
        // console.warn. The shop-profile save remains committed and
        // the user-facing toast still fires.
        //
        // INSERT path (else branch below) is intentionally NOT wired
        // in this round. Shop-profile creation is an edge case
        // (rows are normally created at shop signup via trigger);
        // a future P6-A round may add SHOP_PROFILE_CREATED if
        // operationally needed.
        if (user?.id && shopProfileData) {
          const newRecord = profileData as Record<string, unknown>;
          const oldRecord = shopProfileData as unknown as Record<string, unknown>;
          const changedFields: string[] = [];
          for (const key of Object.keys(newRecord)) {
            if (JSON.stringify(newRecord[key]) !== JSON.stringify(oldRecord[key])) {
              changedFields.push(key);
            }
          }
          if (savedCulture && JSON.stringify(savedCulture) !== JSON.stringify(oldRecord.shop_culture)) {
            changedFields.push('shop_culture');
          }
          const { error: auditErr } = await supabase
            .from('audit_log')
            .insert({
              actor_user_id: user.id,
              shop_id: activeShop.id,
              action: 'SHOP_SETTING_UPDATED',
              object_type: 'shop_profile',
              object_id: activeShop.id,
              new_value: { changed_fields: changedFields },
            });
          if (auditErr) {
            console.warn('[ShopProfile.onSubmit] audit_log insert failed (non-fatal):', auditErr);
          }
        }
      } else if (activeShop?.id) {
        const profileDataWithShopId = {
          ...profileData,
          shop_culture: applyShopProfileCulture(null, values.shopCulture) as never,
          shop_id: activeShop?.id || '',
        };
        const { data, error } = await supabase
          .from('shop_profile')
          .insert([profileDataWithShopId])
          .select()
          .single();
        if (error) throw error;
        if (data) setShopId(data.id);
      }

      toast({
        title: 'Shop profile saved',
        description: 'Your changes have been saved successfully',
      });

      // Codex: Trigger board cascade after shop profile changes (breaks/culture affect scheduling)
      if (activeShop?.id) {
        const shopTz = form.getValues('timeZone') || 'America/Los_Angeles';
        const todayStr = getTodayInShopTz(shopTz);
        triggerBoardCascade(activeShop.id, todayStr).catch(err =>
          console.warn('[ShopProfile] Post-save cascade error:', err)
        );
      }

      // Log policy changes for shop culture sliders (Core Tier Pensieve logging)
      if (activeShop && shopProfileData?.shop_culture && values.shopCulture) {
        const { data: userData } = await supabase.auth.getUser();
        const userId = userData?.user?.id;
        if (userId) {
          const oldCulture = shopProfileData.shop_culture as any;
          const sliderChanges = [
            { name: 'willingness_to_reassign', old: oldCulture.willingness_to_reassign, new: values.shopCulture.willingnessToReassign },
            { name: 'specialist_gap_splitting', old: oldCulture.specialist_gap_splitting, new: values.shopCulture.specialistGapSplitting },
            { name: 'workload_balance_preference', old: oldCulture.workload_balance_preference, new: values.shopCulture.workloadBalancePreference },
            { name: 'comeback_nudge', old: oldCulture.comeback_nudge, new: values.shopCulture.comebackNudge },
            { name: 'willingness_roll_past_eod', old: oldCulture.willingness_roll_past_eod, new: values.shopCulture.willingnessRollPastEod },
            { name: 'present_vehicle_priority', old: oldCulture.present_vehicle_priority, new: values.shopCulture.presentVehiclePriority },
            { name: 'waiter_time_flex_min', old: oldCulture.waiter_time_flex_min, new: values.shopCulture.waiterTimeFlexMin },
            { name: 'no_show_grace_threshold_min', old: oldCulture.no_show_grace_threshold_min, new: values.shopCulture.noShowGraceThresholdMin },
            { name: 'eod_risk_threshold_min', old: oldCulture.eod_risk_threshold_min, new: values.shopCulture.eodRiskThresholdMin },
          ];

          for (const change of sliderChanges) {
            if (change.old !== change.new) {
              logPolicyChange({
                shopId: activeShop.id,
                userId,
                sliderName: change.name,
                oldValue: change.old,
                newValue: change.new,
              }).catch(err => console.warn('[PENSIEVE] Failed to log policy change:', err));
            }
          }
        }
      }


      // Check if vehicle packages changed and sync catalog
      const packagesChanged = JSON.stringify(previousPackages.sort()) !== JSON.stringify(values.vehiclePackages.sort());
      
      if (packagesChanged) {
        setIsImportingCatalog(true);
        
        // Map package IDs to catalog package types
        const packageMapping: Record<string, string> = {
          'asian': 'Asian',
          'domestic': 'Domestic',
          'light_duty_diesel': 'Diesel',
          'euro': 'Euro',
          'ev': 'EV',
          'exotic': 'Exotic',
        };
        
        // Find packages that were removed
        const removedPackages = previousPackages.filter(p => !values.vehiclePackages.includes(p));
        
        // Delete data for removed packages
        if (removedPackages.length > 0) {
          const removedPackageTypes = removedPackages
            .map(id => packageMapping[id])
            .filter(Boolean);
          
          if (removedPackageTypes.length > 0) {
            await supabase
              .from('vehicle_catalog')
              .delete()
              .in('package_type', removedPackageTypes);
            
            toast({
              title: 'Vehicle packages removed',
              description: `Removed ${removedPackageTypes.join(', ')} data from catalog`,
            });
          }
        }
        
        // Import data for newly selected packages
        if (values.vehiclePackages.length > 0) {
          const catalogPackages = values.vehiclePackages
            .map(id => packageMapping[id])
            .filter(Boolean);

          await importVehicleCatalog(
            (message) => console.log('Import progress:', message),
            catalogPackages,
            false // Don't clear all data
          );
        }
        
        setIsImportingCatalog(false);
        setPreviousPackages(values.vehiclePackages);
      }
      // Batch 13 (2026-05-29): navigate-back-to-dashboard moved out of
      // `finally` and into the success branch. Pre-bump the navigate
      // ran on EVERY exit including the catch block — the user got a
      // 200ms destructive-toast flash then landed on the Dashboard, so
      // a real save failure looked like "the button didn't do anything"
      // (the failure was hidden by the immediate route change). Now,
      // errors stay on the page so the operator sees the destructive
      // toast AND the structured console line; success still navigates.
      console.info('[ShopProfile.onSubmit] save complete (success)');
      setTimeout(() => {
        navigate('/');
        // Auto-advance tutorial if on save-profile step
        if (isActive && currentStepData?.id === 'save-profile') {
          setTimeout(() => nextStep(), 100);
        }
      }, 200);
    } catch (error: any) {
      console.error('[ShopProfile.onSubmit] save failed (catch)', { message: error?.message, error });
      toast({
        title: 'Error saving profile',
        description: error.message,
        variant: 'destructive',
      });
      setIsImportingCatalog(false);
      // Intentionally do NOT navigate — keep the user on the form so
      // they can correct the error and retry.
    } finally {
      setIsSaving(false);
    }
  };

// Auto-save timer ref for proper cleanup
const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

// Auto-save and auto-import vehicle packages
useEffect(() => {
  if (!activeShop?.id) return;
  
  const subscription = form.watch((value) => {
    // Clear any pending auto-save
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    // Debounce auto-save
    autoSaveTimerRef.current = setTimeout(async () => {
      const { name, address, timeZone, vehiclePackages } = value;
      
      // Auto-save when required fields have values (name + time zone)
      if (name && timeZone) {
        try {
          // Build hours payload — only include days with valid open/close
          const filteredHours: Record<string, { open: string; close: string }> = {};
          for (const [day, h] of Object.entries(hours)) {
            if (h?.open && h?.close) {
              filteredHours[day] = { open: h.open, close: h.close };
            }
          }

          await supabase
            .from('shop_profile')
            .update({
              name,
              address: address || null,
              time_zone: timeZone,
              vehicle_packages: vehiclePackages || [],
              hours: filteredHours,
              holidays: holidays || [],
            })
            .eq('shop_id', activeShop?.id);

          // Auto-import/remove vehicle packages when changed
          const packagesChanged = JSON.stringify(previousPackages.sort()) !== JSON.stringify((vehiclePackages || []).sort());
          
          if (packagesChanged) {
            const packageMapping: Record<string, string> = {
              'asian': 'Asian',
              'domestic': 'Domestic',
              'light_duty_diesel': 'Diesel',
              'euro': 'Euro',
              'ev': 'EV',
              'exotic': 'Exotic',
            };
            
            // Find packages that were removed
            const removedPackages = previousPackages.filter(p => !(vehiclePackages || []).includes(p));
            
            // Delete data for removed packages
            if (removedPackages.length > 0) {
              const removedPackageTypes = removedPackages
                .map(id => packageMapping[id])
                .filter(Boolean);
              
              if (removedPackageTypes.length > 0) {
                await supabase
                  .from('vehicle_catalog')
                  .delete()
                  .in('package_type', removedPackageTypes);
                
                toast({
                  title: 'Package removed',
                  description: `Removed ${removedPackageTypes.join(', ')} vehicles`,
                });
              }
            }
            
            // Import ALL currently selected packages (not just newly added ones)
            // This ensures we always have complete data
            if ((vehiclePackages || []).length > 0) {
              setIsImportingCatalog(true);
              
              const catalogPackages = (vehiclePackages || [])
                .map(id => packageMapping[id])
                .filter(Boolean);

              await importVehicleCatalog(
                (message) => console.log('Import progress:', message),
                catalogPackages,
                false // Don't clear all data, only re-import selected packages
              );
              
              setIsImportingCatalog(false);
            }
            
            setPreviousPackages(vehiclePackages || []);
          }
        } catch (error) {
          console.error('Auto-save error:', error);
          setIsImportingCatalog(false);
        }
      }
    }, 1000);
  });
  
  return () => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    subscription.unsubscribe();
  };
}, [activeShop?.id, form, previousPackages, hours, holidays]);

  const toggleDayOpen = (day: string) => {
    const isCurrentlyOpen = openDays[day];
    setOpenDays(prev => ({ ...prev, [day]: !isCurrentlyOpen }));
    
    if (isCurrentlyOpen) {
      // Clear hours when marking as closed
      setHours(prev => ({ ...prev, [day]: { open: '', close: '' } }));
    } else {
      // Initialize default hours when toggling a day ON (if not already set)
      setHours(prev => {
        const existing = prev[day];
        if (!existing?.open || !existing?.close) {
          return { ...prev, [day]: { open: '08:00', close: '17:00' } };
        }
        return prev;
      });
    }
  };

  const updateHours = (day: string, field: 'open' | 'close', value: string) => {
    setHours(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value,
      },
    }));
  };

  const toggleHoliday = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const existingIndex = holidays.findIndex(h => h.date === dateStr);
    
    if (existingIndex >= 0) {
      setHolidays(holidays.filter((_, i) => i !== existingIndex));
    } else {
      setHolidays([...holidays, { date: dateStr, name: 'Holiday' }]);
    }
  };

  const isHoliday = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return holidays.some(h => h.date === dateStr);
  };

  const isDayOpen = (date: Date) => {
    const dayOfWeek = getDay(date);
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayName = dayNames[dayOfWeek];
    return openDays[dayName] && !isHoliday(date);
  };

  const getDaysInMonth = () => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    return eachDayOfInterval({ start, end });
  };

  const loadTechnicians = async () => {
    if (!activeShop) return;
    
    try {
      const { data, error } = await supabase
        .from('technicians')
        .select('*')
        .eq('shop_id', activeShop.id)
        .eq('employment_status', 'Active')
        .order('name');
      
      if (error) throw error;
      setTechnicians((data || []).map(t => ({
        ...t,
        proficiencies: (t.proficiencies as Record<string, number>) || {}
      })));
    } catch (error: any) {
      toast({
        title: 'Error loading technicians',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  // handleSyncTechnicianProfiles removed - profiles are now auto-created via create-technician-with-profile edge function

  /**
   * 8G.2D.5 §11 — ONE CONFIRMATION, ONE CANONICAL AUTHORITY CALL.
   *
   * ═══════════════════════════════════════════════════════════════════════
   * WHAT THIS REPLACES, MEASURED
   * ═══════════════════════════════════════════════════════════════════════
   * The browser used to run the whole offboarding as a sequence of
   * independent durable writes:
   *
   *   1. prefetch tickets WHERE assigned_tech_id = id AND status <> 'Deleted'
   *      and treat that list as authoritative membership;
   *   2. `bulkPauseTicketsForTechRemoval`, which closed every OPEN timer
   *      session on those TICKET IDS — technician-blind, so a colleague
   *      still working got stopped, and the departing technician's own
   *      un-reviewed time got stopped too;
   *   3. the same helper then rewrote every matching ticket to Paused with
   *      `paused_by: 'system'`, cleared the bay and set `parking_lot = true`
   *      — a pause nobody performed and a vehicle move nobody made — over a
   *      predicate that did not exclude Completed or Cancelled, so finished
   *      repair orders were rewritten too;
   *   4. and only then archived the technician, in a THIRD write that could
   *      fail on its own and leave every ticket already mutated.
   *
   * All of that is now one server authority. The browser names the person the
   * operator selected and nothing else: no ticket ids, no shop, no actor, no
   * timestamps, no location claims.
   */
  /**
   * What each canonical refusal means to the person who clicked Remove.
   *
   * The authority answers with a named outcome; this is the only place that
   * turns one into English, so a new refusal cannot reach an operator as a raw
   * enum or as a generic failure.
   */
  const TECH_ARCHIVE_REFUSAL_COPY: Record<string, string> = {
    ACTIVE_WORK:
      'This technician has a timer running. Ask them to stop and review their time before removing them. Nothing was changed.',
    PAUSED_WORK:
      'This technician has paused work that has not been resolved. Resolve it before removing them. Nothing was changed.',
    REVIEW_REQUIRED:
      'This technician has time waiting to be reviewed. Finish the review before removing them. Nothing was changed.',
    TIMER_INTEGRITY_BLOCK:
      'This technician\'s timer records disagree and need a person to resolve them. Nothing was changed.',
    NOT_AUTHORIZED:
      'You do not have permission to remove technicians.',
    TECHNICIAN_NOT_FOUND:
      'That technician no longer exists.',
    NOT_AUTHENTICATED:
      'You are signed out. Sign in and try again.',
  };

  const handleDeleteTechnician = async (id: string) => {
    try {
      const { data, error } = await (supabase as any).rpc(
        'archive_technician_if_timer_safe',
        { p_technician_id: id },
      );
      if (error) throw error;

      const outcome = data?.outcome as string | undefined;

      if (outcome !== 'ARCHIVED' && outcome !== 'ALREADY_APPLIED') {
        const err = new Error(
          TECH_ARCHIVE_REFUSAL_COPY[outcome ?? ''] ??
            `This technician could not be archived (${outcome ?? 'unknown outcome'}).`,
        );
        throw err;
      }

      // ══ AFTER COMMIT ONLY ═════════════════════════════════════════════
      // The transaction has committed by the time we are here, so this
      // describes something that is true. The cascade is a DERIVED
      // scheduling consequence — if it fails, the archive still happened and
      // the operator must not be told otherwise.
      if (outcome === 'ARCHIVED') {
        try {
          const { triggerBoardCascade } = await import('@/utils/cascadeTrigger');
          const shopTz = form.getValues('timeZone') || 'America/Los_Angeles';
          const today = getTodayInShopTz(shopTz);
          if (shopId) await triggerBoardCascade(shopId, today);
        } catch (cascadeErr) {
          console.warn('[handleDeleteTechnician] Post-archive cascade failed:', cascadeErr);
        }
      }

      const released = Number(data?.released_count ?? 0);
      toast({
        title:
          outcome === 'ALREADY_APPLIED'
            ? 'Technician already archived'
            : 'Technician removed from active roster',
        // Says what actually happened. The old copy claimed "Tickets moved to
        // parking lot", which described a vehicle movement nobody made.
        description:
          outcome === 'ALREADY_APPLIED'
            ? 'No further changes were made.'
            : released > 0
              ? `${released} active ticket assignment(s) released. Historical records, tickets, and time logs are preserved.`
              : 'Historical records, tickets, and time logs are preserved.',
      });

      queryClient.invalidateQueries({ queryKey: ['former-members', activeShop?.id] });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });

      loadTechnicians();
      setTechToDelete(null);
      if (selectedTechId === id) {
        setSelectedTechId(null);
        setIsEditingTech(false);
      }
    } catch (error: any) {
      toast({
        title: 'Technician not archived',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  // E2EFIX (2026-10-19): Time Clock Reports have no route of their own since 54a4851c0 removed `/time-clock`;
  // they render inline in Admin Settings. Both entry points used to navigate to `/time-clock` and
  // `/time-clock-reports` — unrouted, so the user landed on Not Found. They now open the inline section.
  useEffect(() => {
    if (isLoading || location.hash !== `#${TIME_CLOCK_REPORTS_ANCHOR}`) return;
    document.getElementById(TIME_CLOCK_REPORTS_ANCHOR)?.scrollIntoView({ block: 'start' });
  }, [isLoading, location.hash, location.search]);

  // Automatically open billing tab if specified in URL params
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'billing') {
      // Tab will auto-switch via the defaultValue/controlled pattern
    }
  }, [searchParams]);

  if (isLoading) {
    return (
      <div className="flex flex-col h-screen bg-background">
        <TopBar />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </main>
      </div>
    );
  }

  const tabValue = searchParams.get('tab') || 'general';

  return (
    <div className="relative flex flex-col h-screen overflow-hidden bg-background">
      <div className="tech-grid" />
      <div className="relative z-10 flex flex-col h-full">
      <TopBar />
      
      <main className="flex-1 overflow-auto">
        <div className="shop-profile-wrapper max-w-7xl mx-auto p-6">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">Shop Profile</h1>
              <p className="text-muted-foreground mt-1">Configure your shop's operational settings</p>
            </div>
            <Button variant="outline" onClick={() => navigate('/')}>
              Back to Dashboard
            </Button>
          </div>

          <Tabs value={tabValue} onValueChange={(v) => {
            navigate(`/shop-profile?tab=${v}`);
            // Auto-advance tutorial if on click-technicians-tab or click-culture-tab step
            if (isActive && currentStepData?.id === 'click-technicians-tab' && v === 'technicians') {
              nextStep();
            }
            if (isActive && currentStepData?.id === 'click-culture-tab' && v === 'culture') {
              nextStep();
            }
          }} className="space-y-6">
            {/* Scrollable Tab Strip Container */}
            <div className="relative w-full">
              {/* Left Chevron - Only on phone/tablet when can scroll left */}
              {formFactor !== 'desktop' && canScrollLeft && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10 h-8 w-8 bg-gradient-to-r from-background via-background to-transparent"
                  onClick={scrollTabsLeft}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              )}

              {/* Scrollable Tab Container */}
              <div
                ref={tabsScrollRef}
                className="w-full overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                onScroll={handleTabsScroll}
              >
                <TabsList className={cn(
                  "inline-flex min-w-max gap-1",
                  formFactor === 'phone' && "text-xs px-2",
                  formFactor === 'tablet' && "text-sm px-3"
                )}>
                  <TabsTrigger value="general" data-testid="shopprofile-tab-general">General</TabsTrigger>
                  <TabsTrigger value="hours" data-testid="shopprofile-tab-hours">Hours & Holidays</TabsTrigger>
                  {showTaxesFees && <TabsTrigger value="taxes-fees" data-testid="shopprofile-tab-taxes-fees">Pricing Control</TabsTrigger>}
                  {showLaborPricing && <TabsTrigger value="labor-pricing" data-testid="shopprofile-tab-labor-pricing">Labor Pricing</TabsTrigger>}
                  {showInvoicing && <TabsTrigger value="invoicing" data-testid="shopprofile-tab-invoicing">Invoicing</TabsTrigger>}
                  <TabsTrigger value="culture" data-testid="shopprofile-tab-culture">Shop Culture</TabsTrigger>
                  <TabsTrigger value="technicians" data-testid="shopprofile-tab-technicians">Technicians</TabsTrigger>
                  {canEditShopProfile && <TabsTrigger value="staffschedules" data-testid="shopprofile-tab-staffschedules">Staff Schedules</TabsTrigger>}
                  {canEditShopProfile && <TabsTrigger value="teams" data-testid="shopprofile-tab-teams">Teams</TabsTrigger>}
                  {/* Stage 2 — top-bar Connect button removed; Connect is
                      now reachable as a Shop Profile tab. The tab itself
                      always renders for shop-profile editors; the page it
                      opens (`/connect`) carries its own feature-flag gate
                      so this tab is safe to show unconditionally. */}
                  {canEditShopProfile && <TabsTrigger value="connect" data-testid="shopprofile-tab-connect">Connect</TabsTrigger>}
                  {canEditShopProfile && <TabsTrigger value="former-members">Former Members</TabsTrigger>}
                  {/* Batch 17-A: External Integrations tab. Gated on
                      canManageExternalIntegrations (OWNER / MANAGER /
                      ORG_ADMIN). Same visibility band as Billing. */}
                  {canManageExternalIntegrations && (
                    <TabsTrigger value="integrations" data-testid="shopprofile-tab-integrations">
                      External Integrations
                    </TabsTrigger>
                  )}
                  {/* CORE-BB-GAP-003 — the loop's own health. Manager/owner
                      band, same gate as the other operational tabs. */}
                  {canEditShopProfile && <TabsTrigger value="learning-loop" data-testid="shopprofile-tab-learning-loop">Learning Loop</TabsTrigger>}
                  {canEditShopProfile && <TabsTrigger value="admin" data-testid="shopprofile-tab-admin">Admin Settings</TabsTrigger>}
                </TabsList>
              </div>

              {/* Right Chevron - Only on phone/tablet when can scroll right */}
              {formFactor !== 'desktop' && canScrollRight && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-10 h-8 w-8 bg-gradient-to-l from-background via-background to-transparent"
                  onClick={scrollTabsRight}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              )}
            </div>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit, onValidationError)}>
                <TabsContent value="general" className="space-y-6">
                  {/* Core-100 R2 (CORE-BM): a participant sees their own participation (hidden outside the beta). */}
                  {activeShop?.id && <BetaParticipationCard shopId={activeShop.id} />}
                  <Card data-testid="general-info-card">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Building2 className="h-5 w-5" />
                        General Information
                      </CardTitle>
                      <CardDescription>Basic shop details and branding</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                       <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Shop Name *</FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="Premier Auto Service" disabled={!canEditShopProfile} data-testid="shop-name-input" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="streetAddress"
                          render={({ field }) => (
                            <FormItem className="md:col-span-2">
                              <FormLabel>Street Address</FormLabel>
                              <FormControl>
                                <Input {...field} placeholder="123 Main Street" disabled={!canEditShopProfile} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="city"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>City</FormLabel>
                              <FormControl>
                                <Input {...field} placeholder="Springfield" disabled={!canEditShopProfile} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="state"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>State</FormLabel>
                              <FormControl>
                                <Input {...field} placeholder="IL" disabled={!canEditShopProfile} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="postalCode"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Postal Code</FormLabel>
                              <FormControl>
                                <Input {...field} placeholder="62701" disabled={!canEditShopProfile} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="country"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Country</FormLabel>
                              <FormControl>
                                <Input {...field} placeholder="United States" disabled={!canEditShopProfile} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      {shopProfileData?.shop_lat && shopProfileData?.shop_lng && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mt-2">
                          <MapPin className="h-4 w-4" />
                          <span>Location: {shopProfileData.shop_lat.toFixed(4)}, {shopProfileData.shop_lng.toFixed(4)}</span>
                        </div>
                      )}
                      <FormField
                        control={form.control}
                        name="timeZone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Time Zone *</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value} disabled={!canEditShopProfile}>
                              <FormControl>
                                <SelectTrigger disabled={!canEditShopProfile} data-testid="time-zone-select">
                                  <SelectValue placeholder="Select a time zone" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="America/New_York">Eastern (ET)</SelectItem>
                                <SelectItem value="America/Chicago">Central (CT)</SelectItem>
                                <SelectItem value="America/Denver">Mountain (MT)</SelectItem>
                                <SelectItem value="America/Phoenix">Arizona (MST - No DST)</SelectItem>
                                <SelectItem value="America/Los_Angeles">Pacific (PT)</SelectItem>
                                <SelectItem value="America/Anchorage">Alaska (AKT)</SelectItem>
                                <SelectItem value="Pacific/Honolulu">Hawaii (HT)</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormDescription>Your shop's local time zone</FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </CardContent>
                  </Card>

                  <Card data-testid="vehicle-packages-card">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Wrench className="h-5 w-5" />
                        Vehicle Packages
                      </CardTitle>
                      <CardDescription>Select the types of vehicles your shop services</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <FormField
                        control={form.control}
                        name="vehiclePackages"
                        render={() => (
                          <FormItem>
                            <div className="space-y-4">
                              {vehiclePackages.map((pkg) => (
                                <FormField
                                  key={pkg.id}
                                  control={form.control}
                                  name="vehiclePackages"
                                  render={({ field }) => {
                                    return (
                                      <FormItem
                                        key={pkg.id}
                                        className="flex flex-row items-start space-x-3 space-y-0"
                                      >
                                        <FormControl>
                                          <Checkbox
                                            checked={field.value?.includes(pkg.id)}
                                            disabled={!canEditShopProfile}
                                            onCheckedChange={(checked) => {
                                              return checked
                                                ? field.onChange([...field.value, pkg.id])
                                                : field.onChange(
                                                    field.value?.filter(
                                                      (value) => value !== pkg.id
                                                    )
                                                  )
                                            }}
                                          />
                                        </FormControl>
                                        <div className="space-y-1 leading-none">
                                          <FormLabel className="font-medium">
                                            {pkg.label}
                                          </FormLabel>
                                          <FormDescription className="text-xs">
                                            {pkg.description}
                                          </FormDescription>
                                        </div>
                                      </FormItem>
                                    )
                                  }}
                                />
                              ))}
                            </div>
                          </FormItem>
                        )}
                      />
                    </CardContent>
                  </Card>

                  <Card data-testid="outdoor-weather-card">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <CloudSun className="h-5 w-5" />
                        Outdoor Weather Settings
                      </CardTitle>
                      <CardDescription>
                        Configure weather-based restrictions for outdoor bays. When enabled, the system will check weather conditions before scheduling tickets in outdoor bays.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <FormField
                        control={form.control}
                        name="shopCulture.outdoorWeatherSettings.enabled"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                            <div className="space-y-0.5">
                              <FormLabel className="text-base">Enable Weather Gating</FormLabel>
                              <FormDescription>
                                Block outdoor bay scheduling when weather conditions are poor
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                disabled={!canEditShopProfile}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />

                      {form.watch('shopCulture.outdoorWeatherSettings.enabled') && (
                        <>
                          <FormField
                            control={form.control}
                            name="shopCulture.outdoorWeatherSettings.minOutdoorTempF"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Minimum Temperature (°F)</FormLabel>
                                <FormDescription>Block outdoor bays when temperature is below this threshold</FormDescription>
                                <div className="flex items-center gap-4">
                                  <Slider
                                    min={0}
                                    max={60}
                                    step={5}
                                    value={[field.value ?? 40]}
                                    onValueChange={([value]) => field.onChange(value)}
                                    disabled={!canEditShopProfile}
                                    className="flex-1"
                                  />
                                  <span className="text-sm font-medium w-16 text-right">{field.value || 40}°F</span>
                                </div>
                              </FormItem>
                            )}
                          />

                          <div className="space-y-4">
                            <FormLabel>Block Outdoor Bays When:</FormLabel>
                            <div className="space-y-3 ml-2">
                              <FormField
                                control={form.control}
                                name="shopCulture.outdoorWeatherSettings.blockWhenRaining"
                                render={({ field }) => (
                                  <FormItem className="flex items-center space-x-3 space-y-0">
                                    <FormControl>
                                      <Checkbox
                                        checked={field.value}
                                        onCheckedChange={field.onChange}
                                        disabled={!canEditShopProfile}
                                      />
                                    </FormControl>
                                    <div className="flex items-center gap-2">
                                      <span>🌧️</span>
                                      <FormLabel className="font-normal">Raining</FormLabel>
                                    </div>
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={form.control}
                                name="shopCulture.outdoorWeatherSettings.blockWhenSnowing"
                                render={({ field }) => (
                                  <FormItem className="flex items-center space-x-3 space-y-0">
                                    <FormControl>
                                      <Checkbox
                                        checked={field.value}
                                        onCheckedChange={field.onChange}
                                        disabled={!canEditShopProfile}
                                      />
                                    </FormControl>
                                    <div className="flex items-center gap-2">
                                      <span>❄️</span>
                                      <FormLabel className="font-normal">Snowing</FormLabel>
                                    </div>
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={form.control}
                                name="shopCulture.outdoorWeatherSettings.blockWhenStorms"
                                render={({ field }) => (
                                  <FormItem className="flex items-center space-x-3 space-y-0">
                                    <FormControl>
                                      <Checkbox
                                        checked={field.value}
                                        onCheckedChange={field.onChange}
                                        disabled={!canEditShopProfile}
                                      />
                                    </FormControl>
                                    <div className="flex items-center gap-2">
                                      <span>⛈️</span>
                                      <FormLabel className="font-normal">Thunderstorms</FormLabel>
                                    </div>
                                  </FormItem>
                                )}
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="hours" className="space-y-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card data-testid="business-hours-card">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Clock className="h-5 w-5" />
                          Shop Hours (Per Day)
                        </CardTitle>
                        <CardDescription>Set operating hours for each day</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4" data-testid="business-hours-editor">
                        {daysOfWeek.map(day => (
                          <div key={day} className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Checkbox
                                checked={openDays[day] || false}
                                onCheckedChange={() => toggleDayOpen(day)}
                                disabled={!canEditShopProfile}
                              />
                              <p className="font-medium text-sm w-[100px]">{day}</p>
                            </div>
                            {openDays[day] && (
                              <div className="ml-6 grid grid-cols-[auto_1fr_auto_1fr] gap-2 items-center">
                                <span className="text-sm text-muted-foreground">From</span>
                                <Input
                                  type="time"
                                  value={hours[day]?.open || ''}
                                  onChange={(e) => updateHours(day, 'open', e.target.value)}
                                  className="w-full"
                                  disabled={!canEditShopProfile}
                                />
                                <span className="text-sm text-muted-foreground">To</span>
                                <Input
                                  type="time"
                                  value={hours[day]?.close || ''}
                                  onChange={(e) => updateHours(day, 'close', e.target.value)}
                                  className="w-full"
                                  disabled={!canEditShopProfile}
                                />
                              </div>
                            )}
                          </div>
                        ))}
                      </CardContent>
                    </Card>

                    <Card data-testid="holidays-card">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <CalendarIcon className="h-5 w-5" />
                          Holiday & Special Closure Planner
                        </CardTitle>
                        <CardDescription>
                          Green = open, Red = closed. Click dates to toggle; use arrows to change month.
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                              disabled={!canEditShopProfile}
                            >
                              <ChevronLeft className="h-4 w-4" />
                              Prev
                            </Button>
                            <h3 className="font-semibold">{format(currentMonth, 'MMMM yyyy')}</h3>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                              disabled={!canEditShopProfile}
                            >
                              Next
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </div>

                          <div className="grid grid-cols-7 gap-1">
                            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                              <div key={day} className="text-center text-xs font-medium text-muted-foreground p-2">
                                {day}
                              </div>
                            ))}
                            
                            {/* Empty cells for days before month starts */}
                            {Array.from({ length: (getDay(startOfMonth(currentMonth)) + 6) % 7 }).map((_, i) => (
                              <div key={`empty-${i}`} />
                            ))}
                            
                            {getDaysInMonth().map(date => {
                              const isOpen = isDayOpen(date);
                              return (
                                <Button
                                  key={date.toString()}
                                  type="button"
                                  variant="outline"
                                  className={`h-10 p-0 ${
                                    isOpen 
                                      ? 'bg-green-500 hover:bg-green-600 text-white border-green-600' 
                                      : 'bg-red-500 hover:bg-red-600 text-white border-red-600'
                                  }`}
                                  onClick={() => toggleHoliday(date)}
                                  disabled={!canEditShopProfile}
                                >
                                  {format(date, 'd')}
                                </Button>
                              );
                            })}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                <TabsContent value="culture" className="space-y-6">
                  <Card data-testid="culture-settings-card">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <TrendingUp className="h-5 w-5" />
                        Shop Culture Settings
                      </CardTitle>
                      <CardDescription>
                        Fine-tune how the scheduling system handles assignments and workload distribution. 
                        These settings help optimize efficiency while respecting your shop's operational preferences.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-8">
                      <FormField
                        control={form.control}
                        name="shopCulture.willingnessToReassign"
                        render={({ field }) => (
                          <FormItem>
                            <div className="flex justify-between items-center mb-2">
                              <div className="flex items-center gap-1.5">
                                <FormLabel>Willingness to Reassign</FormLabel>
                                <SliderHelpTooltip
                                  title="Willingness to Reassign"
                                  description={SHOP_CULTURE_HELP.willingnessToReassign.explanation}
                                  example={SHOP_CULTURE_HELP.willingnessToReassign.example}
                                />
                              </div>
                              <span className="text-sm text-muted-foreground font-medium">
                                {field.value}%
                              </span>
                            </div>
                            <FormDescription className="mb-4">
                              How willing the system should be to reassign work from one technician to another. 
                              Higher values allow more flexibility in reassignments.
                            </FormDescription>
                            <FormControl>
                              <Slider
                                value={[field.value ?? 50]}
                                onValueChange={(value) => field.onChange(value[0])}
                                min={0}
                                max={100}
                                step={1}
                                disabled={!canEditShopProfile}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="shopCulture.specialistGapSplitting"
                        render={({ field }) => (
                          <FormItem>
                            <div className="flex justify-between items-center mb-2">
                              <div className="flex items-center gap-1.5">
                                <FormLabel>Skill Tier Gap for Ticket Splitting</FormLabel>
                                <SliderHelpTooltip
                                  title="Skill Tier Gap for Splitting"
                                  description={SHOP_CULTURE_HELP.specialistGapSplitting.explanation}
                                  example={SHOP_CULTURE_HELP.specialistGapSplitting.example}
                                />
                              </div>
                              <span className="text-sm text-muted-foreground font-medium">
                                {field.value ?? 5} {(field.value ?? 5) === 1 ? 'tier' : 'tiers'}
                              </span>
                            </div>
                            <FormDescription className="mb-4">
                              The minimum skill tier difference (1-10 scale) required to split a ticket between technicians. 
                              Lower values (1-2) split tickets more often with smaller tier gaps. 
                              Higher values (8-10) only split when there's a large tier difference. 
                              Set to 0 to disable tier-based splitting.
                            </FormDescription>
                            <FormControl>
                              <Slider
                                value={[field.value ?? 5]}
                                onValueChange={(value) => field.onChange(value[0])}
                                min={0}
                                max={10}
                                step={1}
                                disabled={!canEditShopProfile}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="shopCulture.workloadBalancePreference"
                        render={({ field }) => (
                          <FormItem>
                            <div className="flex justify-between items-center mb-2">
                              <div className="flex items-center gap-1.5">
                                <FormLabel>Workload Balance Preference</FormLabel>
                                <SliderHelpTooltip
                                  title="Workload Balance Preference"
                                  description={SHOP_CULTURE_HELP.workloadBalancePreference.explanation}
                                  example={SHOP_CULTURE_HELP.workloadBalancePreference.example}
                                />
                              </div>
                              <span className="text-sm text-muted-foreground font-medium">
                                {(field.value ?? 0.5).toFixed(2)}
                              </span>
                            </div>
                            <FormDescription className="mb-4">
                              Balance between equal workload distribution and prioritizing less loaded technicians.
                              <br />
                              <span className="font-medium">0 = Strict Balance</span> (spread work evenly across all techs)
                              <br />
                              <span className="font-medium">1 = Specialist Mode</span> (allow clustering work on best-fit techs)
                            </FormDescription>
                            <FormControl>
                              <Slider
                                value={[field.value ?? 0.5]}
                                onValueChange={(value) => field.onChange(value[0])}
                                min={0}
                                max={1}
                                step={0.01}
                                disabled={!canEditShopProfile}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="shopCulture.comebackNudge"
                        render={({ field }) => (
                          <FormItem>
                            <div className="flex justify-between items-center mb-2">
                              <div className="flex items-center gap-1.5">
                                <FormLabel>Comeback Nudge</FormLabel>
                                <SliderHelpTooltip
                                  title="Comeback Nudge"
                                  description={SHOP_CULTURE_HELP.comebackNudge.explanation}
                                  example={SHOP_CULTURE_HELP.comebackNudge.example}
                                />
                              </div>
                              <span className="text-sm text-muted-foreground font-medium">
                                {field.value}
                              </span>
                            </div>
                            <FormDescription className="mb-4">
                              Priority boost for comeback/warranty work. Higher values increase the likelihood that 
                              comeback work is assigned to the original technician who worked on it.
                            </FormDescription>
                            <FormControl>
                              <Slider
                                value={[field.value ?? 100]}
                                onValueChange={(value) => field.onChange(value[0])}
                                min={0}
                                max={100}
                                step={1}
                                disabled={!canEditShopProfile}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="shopCulture.willingnessRollPastEod"
                        render={({ field }) => (
                          <FormItem>
                            <div className="flex justify-between items-center mb-2">
                              <div className="flex items-center gap-1.5">
                                <FormLabel>Willingness to Roll Past EOD</FormLabel>
                                <SliderHelpTooltip
                                  title="Willingness to Roll Past EOD"
                                  description={SHOP_CULTURE_HELP.willingnessRollPastEod.explanation}
                                  example={SHOP_CULTURE_HELP.willingnessRollPastEod.example}
                                />
                              </div>
                              <span className="text-sm text-muted-foreground font-medium">
                                {field.value} minutes
                              </span>
                            </div>
                            <FormDescription className="mb-4">
                              Maximum minutes the system will schedule work beyond the shop's normal closing time. 
                              Set to 0 to strictly enforce end-of-day schedules.
                            </FormDescription>
                            <FormControl>
                              <Slider
                                value={[field.value || 15]}
                                onValueChange={(value) => field.onChange(value[0])}
                                min={0}
                                max={60}
                                step={5}
                                disabled={!canEditShopProfile}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />


                      <FormField
                        control={form.control}
                        name="shopCulture.presentVehiclePriority"
                        render={({ field }) => (
                          <FormItem>
                            <div className="flex justify-between items-center mb-2">
                              <div className="flex items-center gap-1.5">
                                <FormLabel>Present Vehicle Priority</FormLabel>
                                <SliderHelpTooltip
                                  title="Present Vehicle Priority"
                                  description={SHOP_CULTURE_HELP.presentVehiclePriority.explanation}
                                  example={SHOP_CULTURE_HELP.presentVehiclePriority.example}
                                />
                              </div>
                              <span className="text-sm text-muted-foreground font-medium">
                                {field.value} minutes
                              </span>
                            </div>
                            <FormDescription className="mb-4">
                              Time window for prioritizing vehicles already in the shop. Within this window, 
                              present vehicles are moved ahead of scheduled appointments that haven't arrived yet.
                            </FormDescription>
                            <FormControl>
                              <Slider
                                value={[field.value || 25]}
                                onValueChange={(value) => field.onChange(value[0])}
                                min={0}
                                max={120}
                                step={5}
                                disabled={!canEditShopProfile}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="shopCulture.ticketSplittingEnabled"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                            <div className="space-y-0.5">
                              <FormLabel className="text-base">Enable Ticket Splitting</FormLabel>
                              <FormDescription>
                                Allow the optimizer to suggest splitting tickets between technicians based on skill tier gaps.
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                disabled={!canEditShopProfile}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="shopCulture.minSplitMinutes"
                        render={({ field }) => (
                          <FormItem>
                            <div className="flex justify-between items-center mb-2">
                              <div className="flex items-center gap-1.5">
                                <FormLabel>Minimum Size of Each Part When Splitting</FormLabel>
                                <SliderHelpTooltip
                                  title="Minimum Split Size"
                                  description={SHOP_CULTURE_HELP.minSplitMinutes.explanation}
                                  example={SHOP_CULTURE_HELP.minSplitMinutes.example}
                                />
                              </div>
                              <span className="text-sm text-muted-foreground font-medium">
                                {field.value} minutes
                              </span>
                            </div>
                            <FormDescription className="mb-4">
                              Each side of a split must be at least this many minutes of work. Prevents creating tiny split parts.
                            </FormDescription>
                            <FormControl>
                              <Slider
                                value={[field.value || 30]}
                                onValueChange={(value) => field.onChange(value[0])}
                                min={15}
                                max={120}
                                step={5}
                                disabled={!canEditShopProfile || !form.watch('shopCulture.ticketSplittingEnabled')}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="shopCulture.crossTechFitnessThreshold"
                        render={({ field }) => (
                          <FormItem>
                            <div className="flex justify-between items-center mb-2">
                              <div className="flex items-center gap-1.5">
                                <FormLabel>Cross-Tech Reassignment Threshold</FormLabel>
                                <SliderHelpTooltip
                                  title="Cross-Tech Reassignment Threshold"
                                  description={SHOP_CULTURE_HELP.crossTechFitnessThreshold.explanation}
                                  example={SHOP_CULTURE_HELP.crossTechFitnessThreshold.example}
                                />
                              </div>
                              <span className="text-sm text-muted-foreground font-medium">
                                {field.value} points
                              </span>
                            </div>
                            <FormDescription className="mb-4">
                              Minimum improvement in the optimizer's fitness score required before suggesting moving a ticket to a different technician. Higher values = fewer cross-tech suggestions.
                            </FormDescription>
                            <FormControl>
                              <Slider
                                value={[field.value ?? 20]}
                                onValueChange={(value) => field.onChange(value[0])}
                                min={0}
                                max={100}
                                step={5}
                                disabled={!canEditShopProfile}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="shopCulture.proficiencyGapThreshold"
                        render={({ field }) => (
                          <FormItem>
                            <div className="flex justify-between items-center mb-2">
                              <div className="flex items-center gap-1.5">
                                <FormLabel>Proficiency Gap Tolerance</FormLabel>
                                <SliderHelpTooltip
                                  title="Proficiency Gap Tolerance"
                                  description={SHOP_CULTURE_HELP.proficiencyGapThreshold.explanation}
                                  example={SHOP_CULTURE_HELP.proficiencyGapThreshold.example}
                                />
                              </div>
                              <span className="text-sm text-muted-foreground font-medium">
                                {field.value}%
                              </span>
                            </div>
                            <FormDescription className="mb-4">
                              Maximum drop in pure proficiency (%) we'll allow when reassigning to help balance workload or timing. Lower values require better proficiency matches.
                            </FormDescription>
                            <FormControl>
                              <Slider
                                value={[field.value ?? 40]}
                                onValueChange={(value) => field.onChange(value[0])}
                                min={0}
                                max={100}
                                step={5}
                                disabled={!canEditShopProfile}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="shopCulture.waiterTimeFlexMin"
                        render={({ field }) => (
                          <FormItem>
                            <div className="flex justify-between items-center mb-2">
                              <div className="flex items-center gap-1.5">
                                <FormLabel>Waiter Time Flexibility</FormLabel>
                                <SliderHelpTooltip
                                  title="Waiter Time Flexibility"
                                  description={SHOP_CULTURE_HELP.waiterTimeFlexMin.explanation}
                                  example={SHOP_CULTURE_HELP.waiterTimeFlexMin.example}
                                />
                              </div>
                              <span className="text-sm text-muted-foreground font-medium">
                                {field.value || 0} minutes
                              </span>
                            </div>
                            <FormDescription className="mb-4">
                              How many minutes waiter appointments can flex around their scheduled time. OptiAssign tries the exact promised time first, then allows pushback within this window if needed. Set to 0 to strictly honor appointment times.
                            </FormDescription>
                            <FormControl>
                              <Slider
                                value={[field.value || 0]}
                                onValueChange={(value) => field.onChange(value[0])}
                                min={0}
                                max={30}
                                step={5}
                                disabled={!canEditShopProfile}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* oct20e (FD-2026-10-24-A; CORE-BO :59, :93 as amended): the shop's capacity-recovery mode. */}
                      <FormField
                        control={form.control}
                        name="shopCulture.capacityRecoveryAutomatic"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                            <div className="space-y-0.5">
                              <FormLabel className="text-base">Recover opened capacity automatically</FormLabel>
                              <FormDescription>
                                When a technician frees up early, OptiBay moves the next eligible job whose vehicle is already at the shop, on that technician's own schedule, into the opening and tells you what moved and why. It never moves a vehicle that is not here, a waiter before their time, started work, or anything that would break a Due Out, a rollover or the end of the day. On by default. Turn it off to have every opening offered as a suggestion in Open capacity instead.
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value !== false}
                                onCheckedChange={field.onChange}
                                disabled={!canEditShopProfile}
                                data-testid="capacity-recovery-automatic"
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="shopCulture.allowFutureDayPullForward"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                            <div className="space-y-0.5">
                              <FormLabel className="text-base">Fill opened capacity with later-day work</FormLabel>
                              <FormDescription>
                                May opened capacity be filled with work scheduled for a later day when its vehicle is already at the shop? On by default; turn it off to keep later-day work on its own day. A vehicle that is not here is never pulled forward.
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value !== false}
                                onCheckedChange={field.onChange}
                                disabled={!canEditShopProfile}
                                data-testid="allow-future-day-pull-forward"
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />

                      {/* Core-100 R5 (CORE-BI l.47/l.67, CORE-I l.121): the diagnostic-weighting preference. */}
                      <FormField
                        control={form.control}
                        name="shopCulture.diagnosticWeightEnabled"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                            <div className="space-y-0.5">
                              <FormLabel className="text-base">Weigh demonstrated diagnostic competency</FormLabel>
                              <FormDescription>
                                For diagnostic work, among technicians in the same proficiency band, recommend first the one whose diagnoses have proven out (an established record, never a guess). Proficiency still comes first, nobody is ranked down for having no record yet, and the recommendation says when this setting moved someone. Example: two technicians tie on electrical proficiency; the one with an established record of confirmed diagnoses is suggested first, even if the other has fewer minutes booked. Off by default.
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value === true}
                                onCheckedChange={field.onChange}
                                disabled={!canEditShopProfile}
                                data-testid="diagnostic-weight-enabled"
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="shopCulture.noShowGraceThresholdMin"
                        render={({ field }) => (
                          <FormItem>
                            <div className="flex justify-between items-center mb-2">
                              <div className="flex items-center gap-1.5">
                                <FormLabel>No-show grace threshold</FormLabel>
                                <SliderHelpTooltip
                                  title="No-show grace threshold"
                                  description={SHOP_CULTURE_HELP.noShowGraceThresholdMin.explanation}
                                  example={SHOP_CULTURE_HELP.noShowGraceThresholdMin.example}
                                />
                              </div>
                              <span className="text-sm text-muted-foreground font-medium">
                                {field.value ?? 15} minutes
                              </span>
                            </div>
                            <FormDescription className="mb-4">
                              How many minutes after a scheduled technician shift start before OptiBay should treat an unclocked technician as no-show risk. This setting is foundational only right now and does not yet change scheduling behavior.
                            </FormDescription>
                            <FormControl>
                              <Slider
                                value={[field.value ?? 15]}
                                onValueChange={(value) => field.onChange(value[0])}
                                min={0}
                                max={60}
                                step={5}
                                disabled={!canEditShopProfile}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* Core-100 R4 CORE-BI (GAP-001): this value already decided when the
                          board flags an end-of-day risk, but no shop could see or change it. */}
                      <FormField
                        control={form.control}
                        name="shopCulture.eodRiskThresholdMin"
                        render={({ field }) => (
                          <FormItem data-testid="culture-eod-risk-threshold">
                            <div className="flex justify-between items-center mb-2">
                              <div className="flex items-center gap-1.5">
                                <FormLabel>End-of-day risk warning</FormLabel>
                                <SliderHelpTooltip
                                  title="End-of-day risk warning"
                                  description={SHOP_CULTURE_HELP.eodRiskThresholdMin.explanation}
                                  example={SHOP_CULTURE_HELP.eodRiskThresholdMin.example}
                                />
                              </div>
                              <span className="text-sm text-muted-foreground font-medium">
                                {field.value ?? SHOP_CULTURE_DEFAULTS.eod_risk_threshold_min} minutes
                              </span>
                            </div>
                            <FormDescription className="mb-4">
                              Flag a ticket as at risk when its planned finish is within this many minutes of the technician's end of day.
                            </FormDescription>
                            <FormControl>
                              <Slider
                                value={[field.value ?? SHOP_CULTURE_DEFAULTS.eod_risk_threshold_min]}
                                onValueChange={(value) => field.onChange(value[0])}
                                min={0}
                                max={120}
                                step={5}
                                disabled={!canEditShopProfile}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </CardContent>
                  </Card>

                  {/* Preference Learning Settings */}
                  <PensieveSettingsCard />

                  {/* CORE-BN — front-of-house load policy. Persists into the
                      same shop_culture object as the settings above (merged,
                      not replaced). Renders nothing when the capability is
                      not entitled. */}
                  <FohAttentionSettingsCard canEdit={canEditShopProfile} />

                  {/* Core-100 R3 CORE-AK / CORE-AJ: when unaccepted hand-overs and long-running timers are brought
                      to a person (notices only). */}
                  {activeShop?.id && <AlertThresholdsCard shopId={activeShop.id} canEdit={canEditShopProfile} />}
                </TabsContent>

                {/* Board Optimizer Tab */}
                <TabsContent value="optimizer" className="space-y-6">
                  {/* CORE-BQ Slice C — Decision A, stated where it was being
                      misread. A shop looking at this tab is looking at the
                      controls it might switch off, so this is where it needs to
                      know that integrity checking is not one of them. */}
                  <Card className="border-primary/30">
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <ShieldCheck className="h-5 w-5 text-primary" />
                        <CardTitle className="text-base">Integrity checking is always on</CardTitle>
                      </div>
                      <CardDescription>
                        Not a setting, and nothing below turns it off.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm">
                      <p>
                        Every time work is placed on your board, OptiBay checks whether the
                        result is actually possible — two jobs in one bay at the same time, a
                        vehicle in two places at once, work scheduled into the past, a waiter
                        promised a car after the technician has gone home, and the other
                        contradictions listed on your integrity page.
                      </p>
                      <p>
                        <span className="font-medium">Checking never changes anything.</span>{' '}
                        It reports what it found and what it was able to look at. Letting
                        OptiBay act on its own is the separate decision below, and it is off
                        unless you turn it on.
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => navigate('/operational-integrity')}
                      >
                        See what it found
                      </Button>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <TrendingUp className="h-5 w-5" />
                        <CardTitle>Automated Board Optimizer</CardTitle>
                      </div>
                      <CardDescription>
                        Configure the 30-minute heartbeat that automatically optimizes your shop floor by compacting gaps, 
                        preventing EOD overflow, and suggesting better tech placements.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="space-y-0.5">
                            <FormLabel htmlFor="board-auditor-enabled" className="text-base">
                              Enable Board Optimizer
                            </FormLabel>
                            <p className="text-sm text-muted-foreground">
                              Turn on the automated 30-minute optimization heartbeat during business hours
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              id="board-auditor-enabled"
                              type="checkbox"
                              checked={shopProfileData?.board_auditor_enabled || false}
                              onChange={async (e) => {
                                if (!activeShop?.id) return;
                                const { error } = await supabase
                                  .from('shop_profile')
                                  .update({ board_auditor_enabled: e.target.checked })
                                  .eq('shop_id', activeShop.id);

                                if (error) {
                                  toast({
                                    title: 'Error',
                                    description: 'Failed to update optimizer setting',
                                    variant: 'destructive',
                                  });
                                } else {
                                  setShopProfileData(prev => prev ? {...prev, board_auditor_enabled: e.target.checked} : null);
                                  toast({
                                    title: 'Setting Updated',
                                    description: `Board optimizer ${e.target.checked ? 'enabled' : 'disabled'}`,
                                  });
                                }
                              }}
                              disabled={!canEditShopProfile}
                              className="h-5 w-5"
                            />
                          </div>
                        </div>

                            {/* CORE-BQ Slice C — the write authority, named.

                                This replaced a two-option "Optimizer Mode"
                                selector whose second option, "Auto-Safe / For
                                experienced users", silently granted OptiBay
                                permission to rearrange the board by itself. A
                                shop choosing between two auditing modes was
                                deciding something else entirely, and nothing
                                on the screen said so. */}
                            <div className="space-y-4 pt-4 border-t">
                              <div>
                                <FormLabel className="text-base">Autonomous board changes</FormLabel>
                                <p className="text-sm text-muted-foreground mt-1">
                                  A separate decision. With this off, OptiBay only ever proposes
                                  changes and a person applies them.
                                </p>
                              </div>
                              <div className="flex items-start justify-between gap-4 rounded-md border p-3">
                                <div className="space-y-2">
                                  <div className="font-medium">Let OptiBay change the board by itself</div>
                                  <p className="text-sm text-muted-foreground">
                                    When on, the optimizer may reassign work across your whole board
                                    without anyone confirming it — and only for changes that pass every
                                    safety guard listed below. Anything more complex still waits for a person.
                                  </p>
                                  <p className="text-sm text-muted-foreground">
                                    <span className="font-medium text-foreground">This is permission to write to your board.</span>{' '}
                                    It is not needed for integrity checking, which runs either way and
                                    never changes anything.
                                  </p>
                                  <Badge variant="outline">Owners and managers only</Badge>
                                </div>
                                <input
                                  id="autonomous-board-changes"
                                  type="checkbox"
                                  checked={shopProfileData?.autonomous_board_changes_enabled === true}
                                  onChange={async (e) => {
                                    if (!activeShop?.id) return;
                                    const { error } = await supabase
                                      .from('shop_profile')
                                      .update({ autonomous_board_changes_enabled: e.target.checked })
                                      .eq('shop_id', activeShop.id);

                                    if (error) {
                                      toast({
                                        title: 'Error',
                                        description: 'Failed to update autonomous board authority',
                                        variant: 'destructive',
                                      });
                                    } else {
                                      setShopProfileData(prev => prev ? {...prev, autonomous_board_changes_enabled: e.target.checked} : null);
                                      toast({
                                        title: e.target.checked ? 'Autonomous board changes enabled' : 'Autonomous board changes disabled',
                                        description: e.target.checked
                                          ? 'OptiBay may now apply safe board changes without confirmation.'
                                          : 'OptiBay will propose changes only. Integrity checking is unaffected.',
                                      });
                                    }
                                  }}
                                  disabled={!canEditShopProfile}
                                  className="h-5 w-5 mt-1"
                                />
                              </div>
                            </div>
                        {shopProfileData?.board_auditor_enabled && (
                          <>

                            <div className="space-y-3 pt-4 border-t">
                              <FormLabel className="text-base">Safety Guards</FormLabel>
                              <div className="grid gap-2 text-sm">
                                <div className="flex items-start gap-2">
                                  <div className="mt-0.5 h-4 w-4 rounded-full bg-green-500/20 flex items-center justify-center">
                                    <div className="h-2 w-2 rounded-full bg-green-500" />
                                  </div>
                                  <div>
                                    <span className="font-medium">No Past Time:</span> Never schedule work in the past
                                  </div>
                                </div>
                                <div className="flex items-start gap-2">
                                  <div className="mt-0.5 h-4 w-4 rounded-full bg-green-500/20 flex items-center justify-center">
                                    <div className="h-2 w-2 rounded-full bg-green-500" />
                                  </div>
                                  <div>
                                    <span className="font-medium">EOD Guard:</span> Respect technician shift end times + configured buffer
                                  </div>
                                </div>
                                <div className="flex items-start gap-2">
                                  <div className="mt-0.5 h-4 w-4 rounded-full bg-green-500/20 flex items-center justify-center">
                                    <div className="h-2 w-2 rounded-full bg-green-500" />
                                  </div>
                                  <div>
                                    <span className="font-medium">Waiter Protection:</span> Don't move waiter appointments beyond promised times
                                  </div>
                                </div>
                                <div className="flex items-start gap-2">
                                  <div className="mt-0.5 h-4 w-4 rounded-full bg-green-500/20 flex items-center justify-center">
                                    <div className="h-2 w-2 rounded-full bg-green-500" />
                                  </div>
                                  <div>
                                    <span className="font-medium">Lock Respect:</span> Protect tickets manually moved within 15 minutes
                                  </div>
                                </div>
                                <div className="flex items-start gap-2">
                                  <div className="mt-0.5 h-4 w-4 rounded-full bg-green-500/20 flex items-center justify-center">
                                    <div className="h-2 w-2 rounded-full bg-green-500" />
                                  </div>
                                  <div>
                                    <span className="font-medium">Break Conflicts:</span> Avoid placing work over technician breaks
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="p-4 bg-muted/50 rounded-lg">
                              <div className="flex gap-3">
                                <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
                                <div>
                                  <div className="font-medium text-sm">Optimization Schedule</div>
                                  <p className="text-sm text-muted-foreground mt-1">
                                    Runs every <strong>30 minutes</strong> during business hours (defined in Hours & Holidays tab). 
                                    Pauses automatically when shop is closed.
                                  </p>
                                </div>
                              </div>
                            </div>
                          </>
                        )}

                        {/* Time Clock Settings */}
                        <div className="space-y-4 pt-6 border-t mt-6">
                          <div className="flex items-start justify-between">
                            <div className="space-y-1">
                              <FormLabel htmlFor="time-clock-enabled" className="text-base flex items-center gap-2">
                                <Clock className="h-4 w-4" />
                                Time Clock System
                              </FormLabel>
                              <p className="text-sm text-muted-foreground">
                                Enable clock in/out tracking for technicians. Auto-clocks in when starting timers.
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <input
                                id="time-clock-enabled"
                                type="checkbox"
                                checked={shopProfileData?.time_clock_enabled || false}
                                onChange={async (e) => {
                                  if (!activeShop?.id) return;
                                  const { error } = await supabase
                                    .from('shop_profile')
                                    .update({ time_clock_enabled: e.target.checked })
                                    .eq('shop_id', activeShop.id);

                                  if (error) {
                                    toast({
                                      title: 'Error',
                                      description: 'Failed to update time clock setting',
                                      variant: 'destructive',
                                    });
                                  } else {
                                    setShopProfileData(prev => prev ? {...prev, time_clock_enabled: e.target.checked} : null);
                                    toast({
                                      title: 'Setting Updated',
                                      description: `Time clock ${e.target.checked ? 'enabled' : 'disabled'}`,
                                    });
                                  }
                                }}
                                disabled={!canEditShopProfile}
                                className="h-5 w-5"
                              />
                            </div>
                          </div>

                          {shopProfileData?.time_clock_enabled && (
                            <div className="p-4 bg-muted/50 rounded-lg">
                              <div className="flex gap-3">
                                <User className="h-5 w-5 text-muted-foreground mt-0.5" />
                                <div>
                                  <div className="font-medium text-sm">Time Clock Features</div>
                                  <ul className="text-sm text-muted-foreground mt-1 space-y-1">
                                    <li>• Technicians can clock in/out from the dashboard</li>
                                    <li>• Auto-clocks in when starting ticket timers</li>
                                    <li>• After-hours work is automatically tracked</li>
                                    <li>• Managers can view, edit, and export time entries</li>
                                  </ul>
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    className="mt-3"
                                    onClick={() => navigate(TIME_CLOCK_REPORTS_HREF)}
                                  >
                                    View Time Clock Reports
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Technicians tab moved outside the profile form to avoid nested <form> elements */}

                {!isEditingTech && canEditShopProfile && (
                  <div className="flex justify-end gap-3 mt-6">
                    <Button type="submit" disabled={isSaving || isImportingCatalog} data-testid="business-hours-save">
                      {(isSaving || isImportingCatalog) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      <Save className="mr-2 h-4 w-4" />
                      {isImportingCatalog ? 'Importing Vehicle Data...' : isSaving ? 'Saving...' : 'Save Profile'}
                    </Button>
                  </div>
                )}
              </form>
            </Form>
              {/* Technicians tab rendered outside the shop profile form to prevent nested forms */}
              <TabsContent value="technicians" className="space-y-6">
                {!isEditingTech ? (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex-1">
                        <h2 className="text-2xl font-bold">Technician Management</h2>
                        <p className="text-muted-foreground mt-1">
                          Manage your shop's technician roster and skills. Technician profiles are created automatically when team members accept invitations.
                        </p>
                      </div>
                      {/* Sync button removed - profiles are now auto-created with technicians */}
                    </div>
                    
                    {technicians.length === 0 ? (
                      <Card>
                        <CardContent className="flex flex-col items-center justify-center py-12">
                          <Wrench className="h-12 w-12 text-muted-foreground mb-4" />
                          <h3 className="text-lg font-semibold mb-2">No Technicians Yet</h3>
                          <p className="text-muted-foreground text-center mb-4 max-w-md">
                            Technician profiles are automatically created when team members with the Technician role accept their invitation.<br/>
                            Go to <strong>Team Management</strong> to invite technicians.
                          </p>
                          {canEditTechnicians && (
                            <Button onClick={() => navigate('/team-management')}>
                              <UserPlus className="mr-2 h-4 w-4" />
                              Go to Team Management
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    ) : (
                      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {technicians.map(tech => {
                          const profValues = Object.values(tech.proficiencies || {}) as number[];
                          const avgProficiency = profValues.length > 0
                            ? profValues.reduce((sum, val) => sum + val, 0) / profValues.length
                            : 0;

                          // Skill tier display: prefer the stored
                          // technicians.skill_tier when proficiencies
                          // are empty (e.g., fresh wizard-created techs
                          // — commitWizard intentionally writes
                          // proficiencies={} since the manual path has
                          // no inferred work history). Without this
                          // fallback, calculateSkillTier returns the
                          // floor (1) for any tech without proficiency
                          // data, making the Manual Setup wizard
                          // appear to ignore the operator's Skill
                          // slider. For techs WITH real proficiency
                          // data (import path + post-onboarding
                          // accumulation), the derived value continues
                          // to take precedence — it's the more
                          // accurate signal once work history exists.
                          // Shared resolver keeps this card, the /technicians
                          // roster and TechnicianForm's hydrated value in lockstep.
                          const calculatedSkillTier = resolveSkillTier(tech.proficiencies, tech.skill_tier);
                          
                          // Derive specialties from proficiencies (>60)
                          const derivedSpecialties = PROFICIENCY_CATEGORIES.filter(
                            category => (tech.proficiencies?.[category] || 0) > 60
                          );
                          
                          return (
                            <Card key={tech.id} className="hover:shadow-md transition-shadow">
                              <CardHeader className="pb-3">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <CardTitle className="text-lg">{tech.name}</CardTitle>
                                    <p className="text-sm text-muted-foreground mt-1">{tech.role}</p>
                                  </div>
                                  <Badge variant={tech.is_present ? 'default' : 'secondary'}>
                                    {tech.is_present ? 'Present' : 'Away'}
                                  </Badge>
                                </div>
                              </CardHeader>
                              <CardContent className="space-y-3">
                                <div className="flex items-center gap-2 text-sm">
                                  <Award className="h-4 w-4 text-muted-foreground" />
                                  <span className="text-muted-foreground">Skill Tier:</span>
                                  <span className="font-medium">{calculatedSkillTier}/10</span>
                                </div>

                                <div className="flex items-center gap-2 text-sm">
                                  <Clock className="h-4 w-4 text-muted-foreground" />
                                  <span className="text-muted-foreground">Shift:</span>
                                  <span className="font-medium">
                                    {/* TYPICAL WEEK, NOT TODAY.

                                        `shift_start` / `shift_end` are the
                                        MODE across the technician's week, so
                                        Joe D - Monday 07:30-22:00, Tue-Fri
                                        07:30-16:00 - reads as 07:30-16:00
                                        here. This card has no selected-date
                                        context, so it cannot show "today"; it
                                        can only stop claiming to. */}
                                    Typical {formatTimeString(tech.shift_start, 'short')} - {formatTimeString(tech.shift_end, 'short')}
                                    {/* FLOYD-6. The mode is correct and still
                                        misread: after changing Tuesday to
                                        22:00 an operator sees four o'clock
                                        here and thinks the edit was lost.
                                        Said only when the week really has
                                        more than one shape — on a uniform
                                        roster this is noise on a card that
                                        was already right. */}
                                    {hasVariableWorkingHours(tech.hours as never) && (
                                      <span className="text-muted-foreground font-normal">
                                        {' · Varies by day'}
                                      </span>
                                    )}
                                  </span>
                                </div>

                                <div className="grid grid-cols-2 gap-2 text-sm">
                                  <div>
                                    <span className="text-muted-foreground" title={EFFICIENCY_NAMES.planning.scope}>{EFFICIENCY_NAMES.planning.label}:</span>
                                    <span className="ml-1 font-medium">{tech.efficiency_percentage || 100}%</span>
                                  </div>
                                  <div>
                                    <span className="text-muted-foreground">Buffer:</span>
                                    <span className="ml-1 font-medium">{formatPlanningBufferLabel(tech.buffer_percentage)}</span>
                                  </div>
                                </div>

                                <div>
                                  <p className="text-sm text-muted-foreground mb-2">Bay Assignments:</p>
                                  <div className="flex flex-wrap gap-1">
                                    {tech.bay_assignments?.map((bay: string, idx: number) => (
                                      <Badge key={idx} variant="outline" className="text-xs">
                                        {bay}
                                      </Badge>
                                    )) || <span className="text-xs text-muted-foreground">None</span>}
                                  </div>
                                </div>

                                <div>
                                  <p className="text-sm text-muted-foreground mb-2">Specialties:</p>
                                  <div className="flex flex-wrap gap-1">
                                    {derivedSpecialties.map((specialty: string, idx: number) => (
                                      <Badge key={idx} variant="secondary" className="text-xs">
                                        {specialty}
                                      </Badge>
                                    )) || <span className="text-xs text-muted-foreground">None</span>}
                                  </div>
                                </div>

                                <div className="pt-2">
                                  <div className="flex items-center justify-between text-sm mb-1">
                                    <span className="text-muted-foreground">Avg Proficiency</span>
                                    <span className="font-medium">{Math.round(avgProficiency)}%</span>
                                  </div>
                                  <div className="w-full bg-muted rounded-full h-2">
                                    <div 
                                      className="bg-primary h-2 rounded-full transition-all"
                                      style={{ width: `${Math.min(avgProficiency, 100)}%` }}
                                    />
                                 </div>
                                 
                                 {(() => {
                                   // Oct 20 A1: open THIS technician's Passport or be disabled —
                                   // never fall back to the viewer's own /passport.
                                   const target = techPassportTargets?.get(tech.id);
                                   const href = target?.href ?? null;
                                   const reason = target ? target.unavailableReason : 'Checking Passport access…';
                                   return (
                                     <div className="flex flex-col gap-1 pt-2">
                                       <Button
                                         variant="outline"
                                         size="sm"
                                         disabled={!href}
                                         title={href ? undefined : reason ?? undefined}
                                         onClick={() => { if (href) navigate(href); }}
                                         className="flex-1 min-h-11 md:min-h-9"
                                         data-testid={`view-tech-passport-${tech.id}`}
                                       >
                                         View Tech Passport
                                       </Button>
                                       {!href && reason && (
                                         <p className="text-xs text-muted-foreground" data-testid={`view-tech-passport-reason-${tech.id}`}>
                                           {reason}
                                         </p>
                                       )}
                                     </div>
                                   );
                                 })()}
                                </div>

                                {canEditTechnicians && (
                                  <div className="flex gap-2">
                                    <Button 
                                      variant="outline" 
                                      className="flex-1"
                                      onClick={() => {
                                        setSelectedTechId(tech.id);
                                        setIsEditingTech(true);
                                        // Auto-advance tutorial if on click-edit-tech step
                                        if (isActive && currentStepData?.id === 'click-edit-tech') {
                                          nextStep();
                                        }
                                      }}
                                      data-testid="edit-tech-button"
                                    >
                                      <Edit className="mr-2 h-4 w-4" />
                                      Edit Details
                                    </Button>
                                    <Button 
                                      variant="destructive" 
                                      size="icon"
                                      onClick={() => setTechToDelete(tech.id)}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                )}
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setIsEditingTech(false);
                        setSelectedTechId(null);
                      }}
                    >
                      <ChevronLeft className="mr-2 h-4 w-4" />
                      Back to Technicians
                    </Button>
                    <TechnicianForm
                      technicianId={selectedTechId}
                      onSave={() => {
                        setIsEditingTech(false);
                        setSelectedTechId(null);
                        loadTechnicians();
                      }}
                      onCancel={() => {
                        setIsEditingTech(false);
                        setSelectedTechId(null);
                      }}
                    />
                  </div>
                )}
              </TabsContent>

              {/* Staff Schedules tab */}
              {canEditShopProfile && (
                <TabsContent value="staffschedules" className="space-y-6">
                  <StaffScheduleManager />
                </TabsContent>
              )}

              {/* Teams tab - Enterprise multi-team management.
                  Stage 2 — top-bar Team button removed. The full
                  member-management surface (invites, role changes,
                  CRUD) still lives at `/team-management`; the inline
                  card below is the surfaced entry point. */}
              {canEditShopProfile && (
                <TabsContent value="teams" className="space-y-6">
                  <Card data-testid="shopprofile-teams-fullpage-card">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Users className="h-5 w-5" />
                        Team Management
                      </CardTitle>
                      <CardDescription>
                        Invite members, change roles, and manage individual
                        team member access. Opens the full Team Management
                        page.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate('/team-management')}
                        data-testid="shopprofile-teams-fullpage-link"
                      >
                        Open Team Management
                      </Button>
                    </CardContent>
                  </Card>
                  <MultiTeamToggle />
                  <TeamList />
                </TabsContent>
              )}

              {/* Connect tab — Stage 2 navigation cleanup. Top-bar
                  Connect button was removed; this tab surfaces the
                  OptiBay Connect entry inline. The `/connect` route
                  carries the canonical feature-flag and role gating;
                  the button below is a thin navigate. */}
              {canEditShopProfile && (
                <TabsContent value="connect" className="space-y-6" data-testid="shopprofile-tabcontent-connect">
                  <Card data-testid="shopprofile-connect-fullpage-card">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Handshake className="h-5 w-5" />
                        OptiBay Connect
                      </CardTitle>
                      <CardDescription>
                        Manage Connect discoverability, browse the shop and
                        talent directories, and review offers. Opens the
                        full Connect workspace.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate('/connect')}
                        data-testid="shopprofile-connect-fullpage-link"
                      >
                        Open Connect
                      </Button>
                    </CardContent>
                  </Card>
                </TabsContent>
              )}

              {/* Former Members tab */}
              {canEditShopProfile && (
                <TabsContent value="former-members" className="space-y-6">
                  <FormerMembersTab />
                </TabsContent>
              )}

              {/* Batch 17-A: External Integrations tab (read + placeholder UI). */}
              {canManageExternalIntegrations && (
                <TabsContent value="integrations" className="space-y-6">
                  <ExternalIntegrationsTab />
                </TabsContent>
              )}

              {/* Phase 1 + Phase 3: Pricing Control tab (renamed from Taxes & Fees, dual-gated) */}
              {showTaxesFees && shopId && (
                <TabsContent value="taxes-fees" className="space-y-6">
                  <PricingControlTab
                    shopId={activeShop?.id || ''}
                    address={{
                      streetAddress: form.watch('streetAddress') || '',
                      city: form.watch('city') || '',
                      state: form.watch('state') || '',
                      postalCode: form.watch('postalCode') || '',
                      country: form.watch('country') || '',
                    }}
                  />
                </TabsContent>
              )}

              {/* Phase 10 R1 + R5: Labor Pricing tab (dual-gated) */}
              {showLaborPricing && activeShop?.id && (
                <TabsContent value="labor-pricing" className="space-y-6">
                  <LaborPricingTab shopId={activeShop.id} />
                  <FinancialControlsSection />
                </TabsContent>
              )}

              {/* Phase 11 — Customer Invoicing settings (terms + pre-auth toggle) */}
              {showInvoicing && (
                <TabsContent value="invoicing" className="space-y-6">
                  <InvoicingSettingsTab />
                </TabsContent>
              )}

              {/* CORE-BB-GAP-003 — the loop's own health. Read-only
                  projection; see LoopHealthPanel. */}
              {canEditShopProfile && (
                <TabsContent value="learning-loop" className="space-y-6">
                  <LoopHealthPanel shopId={activeShop?.id ?? null} />
                  {/* Core-100 R4 CORE-BC GAP-004: the shop's automation posture, in one place. */}
                  <AutomationPosturePanel shopId={activeShop?.id ?? null} />
                </TabsContent>
              )}

              {/* Admin Settings Hub - Consolidated manager-only features */}
              {canEditShopProfile && (
                <TabsContent value="admin" className="space-y-6">
                  {/* Grid of cards for organization */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    
                    {/* Add-Ons & Features Card */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Package className="h-5 w-5" />
                          Add-Ons & Features
                        </CardTitle>
                        <CardDescription>
                          Enable optional modules to extend your shop's capabilities.
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <ShowFeature feature="ui_optiAssets">
                          <OptiAssetsToggle />
                        </ShowFeature>
                        <OptiBayConnectToggle />
                        <MultiTeamToggle />
                      </CardContent>
                    </Card>

                    {/* Reports & Analytics Card */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <TrendingUp className="h-5 w-5" />
                          Reports & Analytics
                        </CardTitle>
                        <CardDescription>
                          Access performance reports and time tracking.
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {/* CDR-2.2: dual gate — role permission AND ui_payroll_reports
                            exposure flag. The /payroll-reports route is wrapped in
                            <ExposureRouteGate feature="ui_payroll_reports"> at
                            App.tsx, so without this wrap a Core manager would see
                            the button and click into NotFound. */}
                        <ShowFeature feature="ui_payroll_reports">
                          {canViewPayrollReports && (
                            <Button
                              variant="outline"
                              className="w-full justify-start gap-2"
                              onClick={() => navigate('/payroll-reports')}
                            >
                              <DollarSign className="h-4 w-4" />
                              Payroll Reports
                              <ExternalLink className="h-3 w-3 ml-auto opacity-50" />
                            </Button>
                          )}
                        </ShowFeature>
                        {canViewTimeClockReports && (
                          <Button 
                            variant="outline" 
                            className="w-full justify-start gap-2"
                            onClick={() => navigate(TIME_CLOCK_REPORTS_HREF)}
                          >
                            <Clock className="h-4 w-4" />
                            Time Clock Reports
                            <ExternalLink className="h-3 w-3 ml-auto opacity-50" />
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  {/* Communications Card - full width */}
                  <ShowFeature feature="ui_multiChannelComms">
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Bell className="h-5 w-5" />
                          Communications
                        </CardTitle>
                        <CardDescription>
                          Configure customer reminders and notifications.
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <ReminderSettings />
                        <ReminderAnalytics />
                        <SuppressedReminderReasons />
                      </CardContent>
                    </Card>
                  </ShowFeature>

                  {/* TCX-7 — Training domain-match policy (shop scope). Mounted
                      in the Admin Settings tab, which is gated by
                      canEditShopProfile (OWNER/MANAGER). The RPCs re-enforce
                      shop-manager authority server-side. */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Settings className="h-5 w-5" />
                        Training Domain Policy
                      </CardTitle>
                      <CardDescription>
                        Control which domains verify your shop's training evidence.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <TrainingDomainPolicyShopSettings />
                    </CardContent>
                  </Card>

                  {/* Tools & Modules Section */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Settings className="h-5 w-5" />
                        Tools & Modules
                      </CardTitle>
                      <CardDescription>
                        Access addon-gated features and configuration pages.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        <ShowFeature feature="ui_workflowInspections">
                          {optiDvi.isEnabled && canEditInspectionTemplates && (
                            <Button 
                              variant="outline" 
                              onClick={() => navigate('/inspection-templates')}
                              className="gap-2"
                            >
                              <ClipboardList className="h-4 w-4" />
                              Inspection Templates
                            </Button>
                          )}
                        </ShowFeature>
                        <ShowFeature feature="ui_evidenceDiagnostics">
                          {optiDvi.isEnabled && (
                            <Button 
                              variant="outline" 
                              onClick={() => navigate('/dvi-intelligence')}
                              className="gap-2"
                            >
                              <Eye className="h-4 w-4" />
                              DVI Intelligence
                            </Button>
                          )}
                        </ShowFeature>
                        {optiDvi.isEnabled && (
                          <Button
                            variant="outline"
                            onClick={() => navigate('/dvi-learning')}
                            className="gap-2"
                          >
                            <TrendingUp className="h-4 w-4" />
                            DVI Learning Dashboard
                          </Button>
                        )}
                        <ShowFeature feature="ui_optiAssets">
                          {optiAssets.isEnabled && (
                            <Button 
                              variant="outline" 
                              onClick={() => navigate('/optiassets')}
                              className="gap-2"
                            >
                              <Package className="h-4 w-4" />
                              Assets
                            </Button>
                          )}
                        </ShowFeature>
                        {activeShop?.addons?.includes('OPTIQUEUE') && (
                          <Button 
                            variant="outline" 
                            onClick={() => navigate('/optiqueue-settings')}
                            className="gap-2"
                          >
                            <CalendarIcon className="h-4 w-4" />
                            OptiQueue Settings
                          </Button>
                        )}
                        {/* CDR-2.3: /training-catalog is wrapped in
                            <InternalAdminRoute> at App.tsx, so non-admin managers
                            who clicked this button hit the InternalAdminRoute
                            reject. Mirror the route gate at the button. */}
                        {isInternalAdmin && (
                          <Button
                            variant="outline"
                            onClick={() => navigate('/training-catalog')}
                            className="gap-2"
                          >
                            <GraduationCap className="h-4 w-4" />
                            Training Catalog
                          </Button>
                        )}
                        <ShowFeature feature="ui_workflowDiagnostics">
                          {activeShop?.addons?.includes('CATALYST') && (
                            <Button 
                              variant="outline" 
                              onClick={() => navigate('/diagnostic-flows')}
                              className="gap-2"
                            >
                              <Wrench className="h-4 w-4" />
                              Diagnostic Flows
                            </Button>
                          )}
                        </ShowFeature>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Performance Alerts Section */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Bell className="h-5 w-5" />
                        Performance Alerts
                      </CardTitle>
                      <CardDescription>
                        Configure threshold-based alerts for shop performance metrics.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <AlertSettings shopId={activeShop?.id || ''} />
                      <AlertHistoryWidget shopId={activeShop?.id || ''} limit={10} />
                    </CardContent>
                  </Card>

                   {/* Diagnostic Load Guard Settings */}
                   <DlgThresholdSettings />

                   {/* Phone Integration Settings */}
                   <ShowFeature feature="ui_phoneIntegration">
                     <PhoneIntegrationSettings />
                   </ShowFeature>

                   {/* Time Clock Section */}
                  <Card id={TIME_CLOCK_REPORTS_ANCHOR} data-testid="shopprofile-time-clock-reports">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Clock className="h-5 w-5" />
                        Time Clock
                      </CardTitle>
                      <CardDescription>
                        View and manage employee time tracking.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Suspense fallback={<div className="flex items-center justify-center p-8"><Loader2 className="h-8 w-8 animate-spin" /></div>}>
                        <TimeClockReports />
                      </Suspense>
                    </CardContent>
                  </Card>
                  {/* Core-100 R4 (OD-27): Shop ToS §10(a) — the owner closes the shop's account here. The server
                      (close_shop_account) is the authority; the card is shown to owners only. */}
                  {isShopOwner && activeShop?.id && (
                    <CloseShopCard shopId={activeShop.id} shopName={activeShop.name ?? ''} onClosed={() => navigate('/')} />
                  )}
                </TabsContent>
              )}

            </Tabs>
        </div>
      </main>

      <AlertDialog open={!!techToDelete} onOpenChange={(open) => !open && setTechToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Technician from Active Roster</AlertDialogTitle>
            <AlertDialogDescription>
              This will deactivate the technician — they'll no longer appear in
              the active roster or be available for scheduling. Their historical
              tickets, time logs, labor records, and passport remain intact and
              can be restored later if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => techToDelete && handleDeleteTechnician(techToDelete)}
              data-testid="shop-profile-confirm-remove-tech"
            >
              Remove from active roster
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </div>
  );
};

export default ShopProfile;
